import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Book, Code, Terminal, Zap, FileText, ChevronDown, ChevronRight,
  Copy, Check, ExternalLink, HelpCircle, AlertTriangle, Info,
  ArrowLeft, DollarSign, Layout, Bug, Bell, Key, Home, LayoutGrid, Mail, Tag, BarChart2
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getBaseUrl, getSupportEmail, BRAND_NAME, getApiDocsUrl } from "@/config/appConfig";

// Code block component with copy functionality
function CodeBlock({ code, language = "bash", title }) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="relative group">
      {title && (
        <div className="text-xs text-gray-500 mb-1 font-medium">{title}</div>
      )}
      <div className="bg-gray-900 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
          <span className="text-xs text-gray-400">{language}</span>
          <button
            onClick={handleCopy}
            className="text-gray-400 hover:text-white transition-colors p-1"
          >
            {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
        <pre className="p-4 overflow-x-auto text-sm">
          <code className="text-gray-100">{code}</code>
        </pre>
      </div>
    </div>
  );
}

// Expandable section component
function ExpandableSection({ title, icon: Icon, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-violet-600" />}
          <span className="font-medium text-gray-900">{title}</span>
        </div>
        {isOpen ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
      </button>
      {isOpen && (
        <div className="p-4 bg-white border-t border-gray-200">
          {children}
        </div>
      )}
    </div>
  );
}

export default function DeveloperDocsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState(searchParams.get("section") || "quickstart");
  
  // HARDCODED to seatpilot.co.uk
  const baseUrl = "https://seatpilot.co.uk";
  const supportEmail = "support@seatpilot.co.uk";
  const sampleLayoutId = "YOUR_LAYOUT_ID";
  const sampleApiKey = "YOUR_API_KEY";
  
  const sections = [
    { id: "quickstart", label: "Quick Start", icon: Zap },
    { id: "layouts", label: "Layout API", icon: LayoutGrid },
    { id: "pricing", label: "Pricing Setup", icon: DollarSign },
    { id: "ticket-types", label: "Ticket Types", icon: Tag },
    { id: "emails", label: "Email Settings", icon: Mail },
    { id: "widget", label: "Widget Embed", icon: Layout },
    { id: "api", label: "API Reference", icon: Terminal },
    { id: "events", label: "Events & IDs", icon: Key },
    { id: "debugging", label: "Debug & Logs", icon: Bug },
    { id: "webhooks", label: "Webhooks", icon: Bell },
    { id: "analytics", label: "Analytics API", icon: BarChart2 },
    { id: "examples", label: "Code Examples", icon: Book },
    { id: "faq", label: "FAQs", icon: HelpCircle },
  ];
  
  useEffect(() => {
    setSearchParams({ section: activeSection });
  }, [activeSection, setSearchParams]);
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/dashboard?tab=api-keys")}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Back to Dashboard</span>
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-purple-700 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">SP</span>
                </div>
                <span className="font-bold text-gray-900">Developer Docs</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <a
                href={baseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">SeatPilot</span>
              </a>
            </div>
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <nav className="lg:w-64 flex-shrink-0">
            <div className="sticky top-24">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                Documentation
              </h2>
              <ul className="space-y-1">
                {sections.map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        activeSection === section.id
                          ? "bg-violet-100 text-violet-700"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      <section.icon className="w-4 h-4" />
                      {section.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
          
          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Quick Start Section */}
            {activeSection === "quickstart" && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Quick Start Guide</h1>
                  <p className="text-gray-600">Get SeatPilot integrated into your ticketing platform in under 5 minutes with our JavaScript SDK.</p>
                </div>

                <Card className="p-6 bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-200">
                  <h3 className="font-semibold text-gray-900 mb-3">2 Lines of Code to Start</h3>
                  <CodeBlock 
                    code={`<script src="${baseUrl}/sdk/seatpilot.js"></script>
<script>
  const sp = SeatPilot.init({ apiKey: '${sampleApiKey}' });
</script>`}
                    language="html"
                  />
                </Card>

                <Card className="p-5 border border-emerald-200 bg-emerald-50">
                  <h3 className="font-semibold text-emerald-900 mb-2 flex items-center gap-2">
                    <Zap className="w-4 h-4" /> Auto-save is on by default
                  </h3>
                  <p className="text-sm text-emerald-900 mb-2">When you embed the Canvas builder via <code className="bg-white border border-emerald-200 rounded px-1 text-[11px]">sp.builder()</code>, your users' edits are saved <strong>every 5 seconds</strong> to the server. You don't need to wire up a "Save" button.</p>
                  <ul className="text-sm text-emerald-900 space-y-1 list-disc list-inside">
                    <li>Watch the <span className="inline-block w-2 h-2 rounded-full bg-green-500 align-middle"></span> green dot at the top of the Canvas — auto-save is live when green.</li>
                    <li>Press <kbd className="px-1 border border-emerald-300 rounded text-[10px] bg-white">Ctrl/⌘+S</kbd> to force-save immediately, or call <code className="bg-white border border-emerald-200 rounded px-1 text-[11px]">sp.save()</code> from your parent page.</li>
                    <li>Use the <strong>"Save Now"</strong> button (renamed from "Save Draft") in the quick-preset embed widget only when you want to manually commit.</li>
                  </ul>
                </Card>
                
                <Card className="p-6">
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-violet-600 font-bold">1</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-2">Embed the Layout Builder</h3>
                        <p className="text-gray-600 text-sm mb-3">Let users create/edit seating layouts directly on your website. Full drag-and-drop canvas editor.</p>
                        <CodeBlock 
                          code={`// Full canvas builder (create new)
const builder = sp.builder('#my-builder-div');

// Edit existing layout
const editor = sp.builder('#my-builder-div', {
  layoutId: 'LAYOUT_ID',
  onSave: function(layout) {
    console.log('Saved:', layout.layout_id);
    console.log('Seats:', layout.total_seats);
    console.log('Categories:', layout.categories); // [{category, total, seats: [...]}]
  }
});

// Programmatic control
editor.save();    // Trigger save
editor.publish(); // Publish layout`}
                          language="javascript"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-violet-600 font-bold">2</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-2">Add Seat Selection Widget</h3>
                        <p className="text-gray-600 text-sm mb-3">Embed the interactive seat picker on your checkout page.</p>
                        <CodeBlock 
                          code={`// Embed seat selection
const widget = sp.widget('#seat-picker', 'LAYOUT_ID', {
  maxSeats: 6,
  onSeatSelected: (seat) => console.log('Selected:', seat),
  onCheckout: (seats) => {
    // User clicked checkout with these seats
    processBooking(seats);
  }
});`}
                          language="javascript"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-violet-600 font-bold">3</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-2">Set Pricing & Book Seats (API)</h3>
                        <p className="text-gray-600 text-sm mb-3">Set prices, reserve seats, and confirm bookings — all via SDK methods.</p>
                        <CodeBlock 
                          code={`// Set pricing by category
await sp.setPrice('LAYOUT_ID', [
  { category: 'VIP', price: 75 },
  { category: 'Standard', price: 25 }
], 'GBP');

// Get seat details (grouped by category, with seat_id, ref, status)
const seats = await sp.getSeats('LAYOUT_ID');
// seats.categories = [{category: "VIP", total: 50, available: 45, seats: [...]}]

// Calculate total with fees
const total = await sp.calculate('LAYOUT_ID', ['VIP-A1', 'VIP-A2']);
// total.summary = {ticket_subtotal, total_service_fees, total_seat_fees, grand_total}

// Reserve + confirm in one call
const booking = await sp.book('LAYOUT_ID', ['VIP-A1', 'VIP-A2']);

// Or step by step:
const hold = await sp.reserve('LAYOUT_ID', ['VIP-A1', 'VIP-A2'], 15); // 15 min hold
const confirmed = await sp.confirm(hold.hold_token);`}
                          language="javascript"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-violet-600 font-bold">4</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-2">Quick Create Layouts (Zero UI Needed)</h3>
                        <p className="text-gray-600 text-sm mb-3">Create bus or theater layouts with pricing in a single SDK call.</p>
                        <CodeBlock 
                          code={`// Create a bus with pricing in one call
const bus = await sp.createBus({
  name: 'Express Route 42',
  rows: 12,
  seatsPerRow: 4,
  price: 15.00,
  currency: 'GBP'
});
// bus.layout_id → ready to use

// Create a theater with zones
const theater = await sp.createTheater({
  name: 'Grand Hall',
  zones: [
    { name: 'VIP', rows: 3, seats_per_row: 12, price: 100 },
    { name: 'Premium', rows: 5, seats_per_row: 15, price: 60 },
    { name: 'Standard', rows: 8, seats_per_row: 20, price: 30 }
  ],
  currency: 'GBP'
});`}
                          language="javascript"
                        />
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">All SDK Methods</h3>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-mono text-xs text-violet-700 font-semibold">Builder & Widget</p>
                      <code className="text-xs text-gray-600 block mt-1">sp.builder(container, options)</code>
                      <code className="text-xs text-gray-600 block">sp.widget(container, layoutId, options)</code>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-mono text-xs text-violet-700 font-semibold">Layouts</p>
                      <code className="text-xs text-gray-600 block mt-1">sp.getLayouts()</code>
                      <code className="text-xs text-gray-600 block">sp.getLayout(id) / createLayout(data)</code>
                      <code className="text-xs text-gray-600 block">sp.updateLayout(id, data) / deleteLayout(id)</code>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-mono text-xs text-violet-700 font-semibold">Seats & Pricing</p>
                      <code className="text-xs text-gray-600 block mt-1">sp.getSeats(id, {`{category, status}`})</code>
                      <code className="text-xs text-gray-600 block">sp.getSeat(id, seatRef)</code>
                      <code className="text-xs text-gray-600 block">sp.addSeats(id, [{`{seat_ref, category}`}])</code>
                      <code className="text-xs text-gray-600 block">sp.updateSeat(id, ref, {`{category, status}`})</code>
                      <code className="text-xs text-gray-600 block">sp.updateSeats(id, [...]) — bulk</code>
                      <code className="text-xs text-gray-600 block">sp.deleteSeat(id, ref)</code>
                      <code className="text-xs text-gray-600 block">sp.setPrice(id, [{`{category, price}`}])</code>
                      <code className="text-xs text-gray-600 block">sp.assignCategories(id, [...])</code>
                      <code className="text-xs text-gray-600 block">sp.calculate(id, ['A1','A2'])</code>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-mono text-xs text-violet-700 font-semibold">Booking</p>
                      <code className="text-xs text-gray-600 block mt-1">sp.book(id, ['A1','A2']) — reserve+confirm</code>
                      <code className="text-xs text-gray-600 block">sp.reserve(id, refs, mins)</code>
                      <code className="text-xs text-gray-600 block">sp.confirm(token) / sp.release(token)</code>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-mono text-xs text-violet-700 font-semibold">Quick Create</p>
                      <code className="text-xs text-gray-600 block mt-1">sp.createBus({`{name, rows, price}`})</code>
                      <code className="text-xs text-gray-600 block">sp.createTheater({`{name, zones}`})</code>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-mono text-xs text-violet-700 font-semibold">Analytics & Config</p>
                      <code className="text-xs text-gray-600 block mt-1">sp.analytics() / sp.revenue(id)</code>
                      <code className="text-xs text-gray-600 block">sp.trends('daily', 30)</code>
                      <code className="text-xs text-gray-600 block">sp.setServiceFee({`{percent, fixed}`})</code>
                    </div>
                  </div>
                </Card>
                
                <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg">
                  <p className="text-sm text-violet-800">
                    <strong>SDK URL:</strong> <code className="bg-white px-1.5 py-0.5 rounded border border-violet-200">{baseUrl}/sdk/seatpilot.js</code>
                    <br/><br/>
                    <strong>Need help?</strong> Check <button onClick={() => setActiveSection("examples")} className="underline">Code Examples</button> or <button onClick={() => setActiveSection("pricing")} className="underline">Pricing Setup</button> for complete integration guides.
                  </p>
                </div>
              </div>
            )}
            
            {/* Layout API Section - API-First Layout Management */}
            {activeSection === "layouts" && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Layout API</h1>
                  <p className="text-gray-600">Create, manage, and publish seating layouts entirely through our API. Let your event organizers design layouts on YOUR platform.</p>
                </div>
                
                <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg">
                  <p className="text-sm text-violet-800">
                    <strong>🚀 API-First Approach:</strong> Your organizers can create and manage layouts without ever visiting {BRAND_NAME}. Use our API or embed our Layout Builder widget directly in your platform.
                  </p>
                </div>

                {/* 🎯 Create Your First Layout — Interactive Walkthrough */}
                <Card className="p-3 sm:p-6 border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-white">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-4">
                    <div className="min-w-0">
                      <h2 className="text-base sm:text-xl font-semibold flex items-center gap-2">
                        <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-violet-600 shrink-0" />
                        <span>Create Your First Layout in 30 Seconds</span>
                      </h2>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">Pick a preset below, copy the request, run it — you'll have a working layout + widget URL instantly.</p>
                    </div>
                    <span className="self-start sm:self-auto text-xs font-semibold text-violet-700 bg-violet-100 border border-violet-300 rounded-full px-2 py-1 whitespace-nowrap">Interactive Guide</span>
                  </div>

                  {/* Walkthrough steps */}
                  <ol className="space-y-4 mb-6">
                    <li className="flex gap-3">
                      <span className="shrink-0 w-7 h-7 rounded-full bg-violet-600 text-white text-sm font-bold flex items-center justify-center">1</span>
                      <div>
                        <p className="font-medium text-gray-900">Grab your API key</p>
                        <p className="text-sm text-gray-600">Open <a href="/dashboard?tab=api-keys" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">Dashboard → API Keys</a> and copy a key. Every request sends it as <code className="bg-white border border-gray-200 rounded px-1 text-[11px]">X-API-Key: sk_…</code></p>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <span className="shrink-0 w-7 h-7 rounded-full bg-violet-600 text-white text-sm font-bold flex items-center justify-center">2</span>
                      <div>
                        <p className="font-medium text-gray-900">Pick a preset & send the request</p>
                        <p className="text-sm text-gray-600">Each preset below is a single <code className="bg-white border border-gray-200 rounded px-1 text-[11px]">POST /api/external/layouts</code> call. Copy the <code className="bg-white border border-gray-200 rounded px-1 text-[11px]">curl</code>, paste into your terminal, hit enter.</p>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <span className="shrink-0 w-7 h-7 rounded-full bg-violet-600 text-white text-sm font-bold flex items-center justify-center">3</span>
                      <div>
                        <p className="font-medium text-gray-900">Get back a <code className="text-[11px]">layout_id</code> + <code className="text-[11px]">widget_url</code></p>
                        <p className="text-sm text-gray-600">Open the <code className="bg-white border border-gray-200 rounded px-1 text-[11px]">widget_url</code> in your browser — your customer-facing seat picker is live. Or open <code className="bg-white border border-gray-200 rounded px-1 text-[11px]">builder_url</code> to fine-tune on the full Canvas (drag / drop / categories / properties panel — same editor we use on the dashboard).</p>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-600 text-white text-sm font-bold flex items-center justify-center">4</span>
                      <div>
                        <p className="font-medium text-gray-900">Auto-save has your back</p>
                        <p className="text-sm text-gray-600">Once a layout exists, any edits made via the embedded Canvas <strong>auto-save every 5 seconds</strong>. You'll see a green dot at the top when auto-save is on. Need to force a save? Call <code className="bg-white border border-gray-200 rounded px-1 text-[11px]">sp.save()</code> from the SDK or press <kbd className="px-1 border border-gray-300 rounded text-[10px]">Ctrl/⌘+S</kbd>.</p>
                      </div>
                    </li>
                  </ol>

                  {/* Preset tabs — rendered as a stack of collapsible examples */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Presets — Copy, Paste, Done</p>

                    <ExpandableSection title="🎭 Theater rows — 200 seats in 1 call" icon={Code} defaultOpen>
                      <p className="text-sm text-gray-600 mb-2">10 rows × 20 seats with a SCREEN indicator at the top. Perfect for cinemas, concerts, lecture halls.</p>
                      <CodeBlock
                        language="bash"
                        code={`curl -X POST "${baseUrl}/api/external/layouts" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Main Hall",
    "organiser_id": "ORG_123",
    "event_name": "Summer Concert",
    "event_date": "2026-07-15",
    "quick_create": {
      "type": "theater",
      "rows": 10,
      "seats_per_row": 20
    },
    "is_published": true
  }'

# Response → { "layout_id": "…", "seat_count": 200, "widget_url": "…", "builder_url": "…" }`}
                      />
                    </ExpandableSection>

                    <ExpandableSection title="🎉 Banquet tables — weddings, galas, dinners" icon={Code}>
                      <p className="text-sm text-gray-600 mb-2">15 round tables × 8 seats each (120 seats). Each table is a single bookable unit OR book individual seats.</p>
                      <CodeBlock
                        language="bash"
                        code={`curl -X POST "${baseUrl}/api/external/layouts" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Wedding Reception",
    "organiser_id": "ORG_123",
    "quick_create": {
      "type": "banquet",
      "tables": 15,
      "seats_per_table": 8
    },
    "is_published": true
  }'`}
                      />
                    </ExpandableSection>

                    <ExpandableSection title="🚌 Bus / coach — identical to Canvas drag-and-drop" icon={Code}>
                      <div className="text-sm text-gray-600 mb-3 space-y-2">
                        <p><strong>Updated schema (matches the dashboard Canvas exactly)</strong>. Uses <code className="bg-white border border-gray-200 rounded px-1 text-[11px]">seat_pattern</code> like <code className="text-[11px]">"2x2"</code> (2 seats left of aisle, 2 right) or <code className="text-[11px]">"2x3"</code>. The rendered widget now looks <strong>identical</strong> to a bus you'd create by dragging the Bus tool onto the Canvas.</p>
                        <div className="p-2 bg-amber-50 border border-amber-200 rounded text-amber-900 text-xs">
                          <strong>Note:</strong> If you previously used <code className="text-[11px]">rows</code> / <code className="text-[11px]">seats_per_row</code> / <code className="text-[11px]">aisle_after_seat</code>, they still work — we auto-convert them. New integrations should use <code className="text-[11px]">row_count</code> + <code className="text-[11px]">seat_pattern</code>.
                        </div>
                      </div>
                      <CodeBlock
                        language="bash"
                        code={`# New schema — recommended
curl -X POST "${baseUrl}/api/external/layouts" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Tour Bus - London → Edinburgh",
    "organiser_id": "ORG_123",
    "quick_create": {
      "type": "bus",
      "bus_config": {
        "row_count": 9,
        "seat_pattern": "2x2",
        "front_at_top": true,
        "has_driver_seat": true,
        "seat_color": "#22c55e",
        "category": "Standard",
        "price": 45.00
      }
    },
    "is_published": true
  }'

# → Produces a 36-seat coach (9 rows × 4 seats), driver area at front,
#    labels 1A, 1B, 1C, 1D, 2A, … rendering identically to the dashboard Canvas.

# Legacy schema (still supported, auto-converted)
curl -X POST "${baseUrl}/api/external/layouts" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Old-style bus",
    "organiser_id": "ORG_123",
    "quick_create": {
      "type": "bus",
      "bus_config": {
        "rows": 9,
        "seats_per_row": 4,
        "aisle_after_seat": 2
      }
    }
  }'`}
                      />
                    </ExpandableSection>

                    <ExpandableSection title="🎨 Full Canvas control — design it like we do" icon={Code}>
                      <p className="text-sm text-gray-600 mb-2">When you need full control (zones, sections, mixed seating, curved rows, GA pods), either:</p>
                      <ol className="list-decimal list-inside text-sm text-gray-600 mb-3 space-y-1">
                        <li>Send pre-built <code className="bg-white border border-gray-200 rounded px-1 text-[11px]">canvas_objects</code> directly (advanced)</li>
                        <li>Let your users design visually — <strong>embed the same Canvas builder we use on the dashboard</strong> via the SDK (recommended)</li>
                      </ol>
                      <CodeBlock
                        language="html"
                        code={`<!-- Embed the Canvas directly on your page -->
<script src="${baseUrl}/sdk/seatpilot.js"></script>
<div id="builder" style="height: 700px;"></div>
<script>
  const sp = SeatPilot.init({ apiKey: 'YOUR_API_KEY' });

  sp.builder('#builder', {
    mode: 'full',              // Full Canvas (drag tools, properties panel, layers)
    onReady: () => console.log('Canvas ready'),
    onSave:  (layout) => console.log('Saved', layout.layout_id, layout.seat_count),
    onPublish: (layout) => console.log('Published', layout.widget_url),
  });

  // Your users get the EXACT same UI as seatpilot.co.uk:
  //   • Left toolbar: Theater rows, Bus, Banquet, Zones, GA, VIP, Amenities…
  //   • Right panel: Properties, Pricing, Categories, Auto-save indicator
  //   • Top bar: Save Now (force save), Publish, Auto-save status
</script>`}
                      />
                    </ExpandableSection>
                  </div>
                </Card>

                {/* Create Layout via API */}
                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">API Reference — Create Layout</h2>
                  <p className="text-gray-600 mb-4">The endpoint details, full parameter list, and older presets for reference.</p>
                  
                  <ExpandableSection title="Theater Layout (Rows)" icon={Code} defaultOpen>
                    <CodeBlock 
                      code={`curl -X POST "${baseUrl}/api/external/layouts" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Main Hall - Concert",
    "venue_name": "City Concert Hall",
    "organiser_id": "ORG_123",
    "organiser_name": "John Events Ltd",
    "event_id": "EVT_456",
    "event_name": "Summer Concert 2025",
    "quick_create": {
      "type": "theater",
      "rows": 10,
      "seats_per_row": 20
    },
    "is_published": true
  }'`}
                      language="bash"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Banquet Layout (Tables)" icon={Code}>
                    <CodeBlock 
                      code={`curl -X POST "${baseUrl}/api/external/layouts" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Gala Dinner",
    "organiser_id": "ORG_123",
    "quick_create": {
      "type": "banquet",
      "tables": 15,
      "seats_per_table": 8
    }
  }'`}
                      language="bash"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Bus/Coach Layout" icon={Code}>
                    <p className="text-sm text-gray-600 mb-3">Matches the dashboard Canvas shape — same seat labels (1A, 1B, 1C, 1D), same driver area, same rendering.</p>
                    <CodeBlock 
                      code={`curl -X POST "${baseUrl}/api/external/layouts" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Tour Bus - London Trip",
    "organiser_id": "ORG_123",
    "quick_create": {
      "type": "bus",
      "bus_config": {
        "row_count": 12,
        "seat_pattern": "2x2",
        "front_at_top": true,
        "has_driver_seat": true,
        "category": "Standard",
        "price": 45.00
      }
    }
  }'`}
                      language="bash"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Custom Row Configuration with Pricing" icon={Code}>
                    <p className="text-sm text-gray-600 mb-3">For fine-grained control, define individual rows with custom pricing and categories.</p>
                    <CodeBlock 
                      code={`curl -X POST "${baseUrl}/api/external/layouts" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Premium Concert Setup",
    "organiser_id": "ORG_123",
    "rows": [
      { "row_label": "A", "seat_count": 15, "category": "VIP", "price": 150.00, "seat_color": "#fde047" },
      { "row_label": "B", "seat_count": 18, "category": "Premium", "price": 100.00 },
      { "row_label": "C", "seat_count": 20, "category": "Standard", "price": 50.00 }
    ]
  }'`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>
                
                {/* Layout CRUD Operations */}
                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Layout Management</h2>
                  
                  <ExpandableSection title="Get Layout Details" icon={Code} defaultOpen>
                    <CodeBlock 
                      code={`# Get single layout with seat stats
curl -X GET "${baseUrl}/api/external/layouts/{layout_id}" \\
  -H "X-API-Key: YOUR_API_KEY"

# List all layouts for an organiser
curl -X GET "${baseUrl}/api/external/layouts?organiser_id=ORG_123" \\
  -H "X-API-Key: YOUR_API_KEY"

# Filter by publish status
curl -X GET "${baseUrl}/api/external/layouts?is_published=true" \\
  -H "X-API-Key: YOUR_API_KEY"`}
                      language="bash"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Update Layout" icon={Code}>
                    <p className="text-sm text-gray-600 mb-3">Update layout details or reconfigure seating. Note: Cannot modify layouts with booked seats.</p>
                    <CodeBlock 
                      code={`curl -X PUT "${baseUrl}/api/external/layouts/{layout_id}" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Updated Layout Name",
    "event_name": "New Event Name",
    "event_date": "2025-08-20"
  }'`}
                      language="bash"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Publish / Unpublish Layout" icon={Code}>
                    <CodeBlock 
                      code={`# Publish layout (make available for booking)
curl -X PATCH "${baseUrl}/api/external/layouts/{layout_id}/publish?is_published=true" \\
  -H "X-API-Key: YOUR_API_KEY"

# Unpublish layout (hide from booking)
curl -X PATCH "${baseUrl}/api/external/layouts/{layout_id}/publish?is_published=false" \\
  -H "X-API-Key: YOUR_API_KEY"`}
                      language="bash"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Delete & Duplicate Layout" icon={Code}>
                    <CodeBlock 
                      code={`# Delete layout (fails if bookings exist)
curl -X DELETE "${baseUrl}/api/external/layouts/{layout_id}" \\
  -H "X-API-Key: YOUR_API_KEY"

# Force delete (use with caution!)
curl -X DELETE "${baseUrl}/api/external/layouts/{layout_id}?force=true" \\
  -H "X-API-Key: YOUR_API_KEY"

# Duplicate layout for recurring events
curl -X POST "${baseUrl}/api/external/layouts/{layout_id}/duplicate?new_name=Copy" \\
  -H "X-API-Key: YOUR_API_KEY"`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>
                
                {/* Embed Layout Builder */}
                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <ExternalLink className="w-5 h-5 text-violet-600" />
                    Embed Layout Builder on Your Platform
                  </h2>
                  <p className="text-gray-600 mb-4">Let your organizers design layouts visually without leaving your platform.</p>
                  
                  <ExpandableSection title="JavaScript SDK Integration" icon={Code} defaultOpen>
                    <CodeBlock 
                      code={`<!-- Include the SDK -->
<script src="${baseUrl}/builder.js"></script>

<!-- Container for the builder -->
<div id="seatpilot-builder"></div>

<script>
  SeatPilot.builder({
    apiKey: 'YOUR_API_KEY',
    container: '#seatpilot-builder',
    userId: 'YOUR_ORGANISER_ID',
    eventId: 'YOUR_EVENT_ID',
    theme: 'light',
    height: '700px',
    
    onSave: function(layout) {
      console.log('Layout saved:', layout.id);
    },
    onPublish: function(layout) {
      console.log('Layout published:', layout.id);
    }
  });
</script>`}
                      language="html"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="SDK Methods" icon={Code}>
                    <CodeBlock 
                      code={`const builder = SeatPilot.builder({
  apiKey: 'YOUR_API_KEY',
  container: '#seatpilot-builder',
  userId: 'YOUR_ORG_ID',
  eventId: 'EVENT_123',          // Optional: link to event
  layoutId: 'LAYOUT_ID',        // Optional: pass to EDIT existing layout

  // Lifecycle callbacks
  onReady: function() {
    console.log("Builder loaded and ready");
  },
  onLoading: function(isLoading) {
    // Show/hide your own loading spinner
    document.getElementById('my-spinner').style.display = isLoading ? 'block' : 'none';
  },
  onSave: function(layout) {
    // Full layout details returned (same as GET /external/layouts/:id)
    console.log("Layout saved:", layout);
    console.log("Layout ID:", layout.layout_id);
    console.log("Name:", layout.name);
    console.log("Seats:", layout.seat_count);
    console.log("Categories:", layout.categories);
    console.log("Published:", layout.is_published);
    console.log("Widget URL:", layout.widget_url);
    
    // Save to your server
    saveLayoutToServer(layout);
  },
  onPublish: function(layout) {
    console.log("Layout published!", layout.layout_id);
    console.log("Widget URL:", layout.widget_url);
  },
  onError: function(error) {
    console.error("Builder error:", error);
  }
});

// Programmatic control
builder.save();                  // Trigger save
builder.publish();               // Save + publish
builder.reset();                 // Reset builder
builder.loadLayout('LAYOUT_ID'); // Load layout for editing

// Layout management via API
builder.getLayouts();                       // List all layouts
builder.getLayout('layout_id');             // Get specific layout
builder.createLayout({name: 'Hall A'});     // Create layout
builder.deleteLayout('layout_id');          // Delete layout

// Ticket types
builder.addTicketTypes('layout_id', [
  { ticket_type_id: 'tt_vip', type: 'paid', name: 'VIP', price: 75,
    available_quantity: 50, sales_start: '...', sales_end: '...' }
]);

// Assign to zones/rows/seats
builder.assignTicketTypes('layout_id', [
  { ticket_type_id: 'tt_vip', assignment_type: 'zone', target: 'VIP Zone', priority: 1 }
]);

// Get availability with resolved ticket types
const avail = await builder.getAvailabilityWithTicketTypes('layout_id');
console.log(avail.seats[0].ticket_types);`}
                      language="javascript"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Direct iframe Embed" icon={Code}>
                    <CodeBlock 
                      code={`<!-- Quick Create (simple wizard) -->
<iframe 
  src="${baseUrl}/embed/builder?api_key=YOUR_API_KEY&organiser_id=YOUR_ORG_ID&theme=light"
  width="100%" 
  height="700"
  frameborder="0"
></iframe>

<!-- Full Canvas Editor (edit existing layout - drag & drop) -->
<iframe 
  src="${baseUrl}/embed/builder?api_key=YOUR_API_KEY&organiser_id=YOUR_ORG_ID&layout_id=LAYOUT_ID&theme=light"
  width="100%" 
  height="800"
  frameborder="0"
></iframe>

<!-- Full Canvas Editor (create new - full drag & drop builder) -->
<iframe 
  src="${baseUrl}/embed/builder?api_key=YOUR_API_KEY&organiser_id=YOUR_ORG_ID&mode=full&theme=light"
  width="100%" 
  height="800"
  frameborder="0"
></iframe>

<!-- 
  mode options:
  - 'auto' (default): quick wizard for new, full editor for existing
  - 'quick': always show simple wizard
  - 'full': always show full canvas editor (drag & drop, zones, rows, objects)
-->

<!-- Listen for events from builder -->
<script>
window.addEventListener('message', function(event) {
  switch(event.data.type) {
    case 'builder_ready':
      console.log('Builder ready, layout:', event.data.layoutId);
      break;
    case 'builder_loading':
      // Show/hide your own loading indicator
      toggleSpinner(event.data.loading);
      break;
    case 'layout_saved':
      // Full layout details returned
      const layout = event.data.layout;
      console.log('Saved:', layout.layout_id);
      console.log('Name:', layout.name);
      console.log('Seats:', layout.seat_count);
      console.log('Published:', layout.is_published);
      console.log('Widget:', layout.widget_url);
      break;
    case 'layout_published':
      console.log('Published!', event.data.layout.widget_url);
      break;
    case 'error':
      console.error('Builder error:', event.data.message);
      break;
  }
});

// Send commands to builder
const iframe = document.querySelector('iframe');
const SP_ORIGIN = '${baseUrl}'; // Restrict messages to SeatPilot origin
iframe.contentWindow.postMessage({ type: 'save_layout' }, SP_ORIGIN);     // Trigger save
iframe.contentWindow.postMessage({ type: 'publish_layout' }, SP_ORIGIN);  // Publish
iframe.contentWindow.postMessage({ type: 'load_layout', layout_id: 'NEW_ID' }, SP_ORIGIN); // Load different layout
</script>`}
                      language="html"
                    />
                  </ExpandableSection>
                </Card>
                
                {/* Pricing Configuration */}
                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">💰 Seat Fee Pricing (B2B)</h2>
                  <p className="text-gray-600 mb-4">
                    Configure the seat fees you charge to your event organisers. Similar to how Stripe works - 
                    you set your own pricing while {BRAND_NAME} charges you based on your subscription plan.
                  </p>
                  
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
                    <p className="text-sm text-amber-800">
                      <strong>📊 Pricing Model:</strong><br/>
                      • <strong>PAYG Plan:</strong> £0.50 per seat<br/>
                      • <strong>Monthly Plan:</strong> £50/month for 10,000 seats, then £0.30/seat overage<br/>
                      • <strong>Enterprise:</strong> Custom pricing<br/>
                      • You charge organisers your configured fee and keep the margin!
                    </p>
                  </div>
                  
                  <ExpandableSection title="Get Your Pricing Configuration" icon={Code} defaultOpen>
                    <CodeBlock 
                      code={`curl -X GET "${baseUrl}/api/external/pricing" \\
  -H "X-API-Key: YOUR_API_KEY"

# Response (varies by your subscription plan):
{
  "pricing": {
    "seat_fee": 0.80,
    "currency": "GBP",
    "fee_name": "Seating Fee"
  },
  "seatpilot_fee": {
    "per_seat": 0.50,
    "plan_id": "pay_as_you_go",
    "plan_name": "Pay As You Go",
    "has_allowance": false,
    "overage_fee": 0.50
  },
  "your_margin": 0.30
}`}
                      language="bash"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Get Available Plans" icon={Code}>
                    <CodeBlock 
                      code={`curl -X GET "${baseUrl}/api/external/pricing/plans" \\
  -H "X-API-Key: YOUR_API_KEY"

# Returns: free_trial, pay_as_you_go, monthly, enterprise plans`}
                      language="bash"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Update Your Pricing" icon={Code}>
                    <p className="text-sm text-gray-600 mb-3">Set your fee per seat. Must be at least your plan's minimum fee.</p>
                    <CodeBlock 
                      code={`curl -X PUT "${baseUrl}/api/external/pricing" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "seat_fee": 0.80,
    "currency": "GBP",
    "fee_name": "Seating Service Fee",
    "pass_to_organiser": true,
    "pass_to_customer": true
  }'`}
                      language="bash"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Calculate Fees" icon={Code}>
                    <CodeBlock 
                      code={`curl -X GET "${baseUrl}/api/external/pricing/calculate?seat_count=100" \\
  -H "X-API-Key: YOUR_API_KEY"

# Response:
{
  "calculation": {
    "seat_count": 100,
    "total": {
      "organiser_pays": 80.00,
      "seatpilot_receives": 50.00,
      "your_profit": 30.00
    }
  }
}`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>
                
                {/* PHP Integration Examples */}
                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    🐘 PHP Integration
                  </h2>
                  <p className="text-gray-600 mb-4">Complete PHP examples for integrating {BRAND_NAME} into your ticketing platform or website.</p>
                  
                  <ExpandableSection title="PHP: SeatPilot API Client Class" icon={Code} defaultOpen>
                    <p className="text-sm text-gray-600 mb-3">A reusable PHP class to interact with the SeatPilot API:</p>
                    <CodeBlock 
                      code={`<?php
/**
 * SeatPilot API Client for PHP
 * Integration for ticketing platforms
 */
class SeatPilotClient {
    private $apiKey;
    private $baseUrl = '${baseUrl}/api';
    
    public function __construct($apiKey) {
        $this->apiKey = $apiKey;
    }
    
    private function request($method, $endpoint, $data = null) {
        $url = $this->baseUrl . $endpoint;
        
        $headers = [
            'X-API-Key: ' . $this->apiKey,
            'Content-Type: application/json'
        ];
        
        $ch = curl_init();
        curl_setopt($ch, CURLOPT_URL, $url);
        curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
        curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
        
        if ($method === 'POST') {
            curl_setopt($ch, CURLOPT_POST, true);
            curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
        } elseif ($method === 'PUT') {
            curl_setopt($ch, CURLOPT_CUSTOMREQUEST, 'PUT');
            curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
        } elseif ($method === 'PATCH') {
            curl_setopt($ch, CURLOPT_CUSTOMREQUEST, 'PATCH');
            curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
        } elseif ($method === 'DELETE') {
            curl_setopt($ch, CURLOPT_CUSTOMREQUEST, 'DELETE');
        }
        
        $response = curl_exec($ch);
        $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
        curl_close($ch);
        
        return [
            'status' => $httpCode,
            'data' => json_decode($response, true)
        ];
    }
    
    // Layout Methods
    public function createLayout($layoutData) {
        return $this->request('POST', '/external/layouts', $layoutData);
    }
    
    public function getLayouts($organiserId = null, $isPublished = null) {
        $params = [];
        if ($organiserId) $params['organiser_id'] = $organiserId;
        if ($isPublished !== null) $params['is_published'] = $isPublished ? 'true' : 'false';
        $query = !empty($params) ? '?' . http_build_query($params) : '';
        return $this->request('GET', '/external/layouts' . $query);
    }
    
    public function getLayout($layoutId) {
        return $this->request('GET', '/external/layouts/' . $layoutId);
    }
    
    public function updateLayout($layoutId, $data) {
        return $this->request('PUT', '/external/layouts/' . $layoutId, $data);
    }
    
    public function deleteLayout($layoutId, $force = false) {
        $query = $force ? '?force=true' : '';
        return $this->request('DELETE', '/external/layouts/' . $layoutId . $query);
    }
    
    public function publishLayout($layoutId, $publish = true) {
        return $this->request('PATCH', '/external/layouts/' . $layoutId . '/publish?is_published=' . ($publish ? 'true' : 'false'));
    }
    
    public function duplicateLayout($layoutId, $newName = null) {
        $query = $newName ? '?new_name=' . urlencode($newName) : '';
        return $this->request('POST', '/external/layouts/' . $layoutId . '/duplicate' . $query);
    }
    
    // Pricing Methods
    public function getPricing() {
        return $this->request('GET', '/external/pricing');
    }
    
    public function updatePricing($pricingData) {
        return $this->request('PUT', '/external/pricing', $pricingData);
    }
    
    public function calculateFees($seatCount) {
        return $this->request('GET', '/external/pricing/calculate?seat_count=' . $seatCount);
    }
    
    public function getPlans() {
        return $this->request('GET', '/external/pricing/plans');
    }
}`}
                      language="php"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="PHP: Create Theater Layout" icon={Code}>
                    <CodeBlock 
                      code={`<?php
require_once 'SeatPilotClient.php';

$client = new SeatPilotClient('YOUR_API_KEY');

// Create a theater-style layout for an event
$result = $client->createLayout([
    'name' => 'Main Hall - Summer Concert',
    'venue_name' => 'City Concert Hall',
    'organiser_id' => 'ORG_' . $yourOrganiserId,  // Your organiser's ID
    'organiser_name' => $organiserName,
    'event_id' => 'EVT_' . $yourEventId,  // Your event ID
    'event_name' => 'Summer Concert 2025',
    'event_date' => '2025-07-15',
    'event_time' => '19:30',
    'quick_create' => [
        'type' => 'theater',
        'rows' => 15,
        'seats_per_row' => 25
    ],
    'is_published' => false  // Start as draft
]);

if ($result['status'] === 200 && $result['data']['success']) {
    $layoutId = $result['data']['layout_id'];
    $seatCount = $result['data']['seat_count'];
    $builderUrl = $result['data']['builder_url'];
    $widgetUrl = $result['data']['widget_url'];
    $embedCode = $result['data']['embed_code'];
    
    echo "Layout created! ID: $layoutId\\n";
    echo "Total seats: $seatCount\\n";
    echo "Builder URL: $builderUrl\\n";
    
    // Store layoutId in your database linked to your event
    saveLayoutToDatabase($yourEventId, $layoutId);
} else {
    echo "Error: " . $result['data']['detail'];
}`}
                      language="php"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="PHP: Create Banquet Layout with Tables" icon={Code}>
                    <CodeBlock 
                      code={`<?php
$client = new SeatPilotClient('YOUR_API_KEY');

// Create banquet layout for gala dinner
$result = $client->createLayout([
    'name' => 'Annual Gala Dinner',
    'venue_name' => 'Grand Ballroom',
    'organiser_id' => 'ORG_' . $organiserId,
    'event_id' => 'EVT_' . $eventId,
    'quick_create' => [
        'type' => 'banquet',
        'tables' => 20,
        'seats_per_table' => 10
    ]
]);

// Or create with custom table configurations
$result = $client->createLayout([
    'name' => 'VIP Dinner Event',
    'organiser_id' => 'ORG_' . $organiserId,
    'tables' => [
        ['table_label' => 'VIP-1', 'table_type' => 'round', 'seat_count' => 8, 'price_per_seat' => 150.00],
        ['table_label' => 'VIP-2', 'table_type' => 'round', 'seat_count' => 8, 'price_per_seat' => 150.00],
        ['table_label' => 'A1', 'table_type' => 'round', 'seat_count' => 10, 'price_per_seat' => 75.00],
        ['table_label' => 'A2', 'table_type' => 'round', 'seat_count' => 10, 'price_per_seat' => 75.00],
    ]
]);`}
                      language="php"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="PHP: Create Bus/Coach Layout" icon={Code}>
                    <CodeBlock 
                      code={`<?php
$client = new SeatPilotClient('YOUR_API_KEY');

// Create bus layout for tour
$result = $client->createLayout([
    'name' => 'London Day Trip - Coach A',
    'organiser_id' => 'ORG_' . $organiserId,
    'event_id' => 'TRIP_' . $tripId,
    'event_name' => 'London Sightseeing Tour',
    'event_date' => '2025-06-20',
    'quick_create' => [
        'type' => 'bus',
        'bus_config' => [
            'rows' => 12,
            'seats_per_row' => 4,
            'aisle_after_seat' => 2,  // Aisle between seats 2 and 3
            'driver_area' => true,
            'toilet_at_back' => true
        ]
    ]
]);

if ($result['status'] === 200) {
    echo "Bus layout created with " . $result['data']['seat_count'] . " seats";
}`}
                      language="php"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="PHP: Custom Rows with Pricing Categories" icon={Code}>
                    <CodeBlock 
                      code={`<?php
$client = new SeatPilotClient('YOUR_API_KEY');

// Create layout with custom pricing per row
$result = $client->createLayout([
    'name' => 'Premium Concert - Custom Pricing',
    'organiser_id' => 'ORG_' . $organiserId,
    'event_id' => 'EVT_' . $eventId,
    'rows' => [
        [
            'row_label' => 'A',
            'seat_count' => 20,
            'category' => 'VIP Front Row',
            'price' => 200.00,
            'seat_color' => '#fde047'  // Gold
        ],
        [
            'row_label' => 'B',
            'seat_count' => 22,
            'category' => 'VIP',
            'price' => 150.00,
            'seat_color' => '#fde047'
        ],
        [
            'row_label' => 'C',
            'seat_count' => 24,
            'category' => 'Premium',
            'price' => 100.00,
            'seat_color' => '#a78bfa'  // Purple
        ],
        [
            'row_label' => 'D',
            'seat_count' => 24,
            'category' => 'Premium',
            'price' => 100.00,
            'seat_color' => '#a78bfa'
        ],
        [
            'row_label' => 'E',
            'seat_count' => 26,
            'category' => 'Standard',
            'price' => 50.00,
            'seat_color' => '#60a5fa'  // Blue
        ],
        [
            'row_label' => 'F',
            'seat_count' => 26,
            'category' => 'Standard',
            'price' => 50.00,
            'seat_color' => '#60a5fa'
        ]
    ],
    'show_screen' => true,
    'screen_label' => 'STAGE'
]);`}
                      language="php"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="PHP: List & Manage Layouts" icon={Code}>
                    <CodeBlock 
                      code={`<?php
$client = new SeatPilotClient('YOUR_API_KEY');

// Get all layouts for an organiser
$result = $client->getLayouts($organiserId);
foreach ($result['data']['layouts'] as $layout) {
    echo $layout['name'] . ' - ' . ($layout['is_published'] ? 'Published' : 'Draft') . "\\n";
    echo '  Seats: ' . $layout['seat_stats']['total'] . "\\n";
    echo '  Available: ' . $layout['seat_stats']['available'] . "\\n";
}

// Get only published layouts
$published = $client->getLayouts($organiserId, true);

// Get single layout with full details
$layout = $client->getLayout($layoutId);
$seats = $layout['data']['seats'];  // Array of all seats with status

// Update layout metadata
$client->updateLayout($layoutId, [
    'name' => 'Updated Event Name',
    'event_date' => '2025-08-01'
]);

// Publish layout (make available for booking)
$client->publishLayout($layoutId, true);

// Unpublish layout
$client->publishLayout($layoutId, false);

// Duplicate layout for recurring event
$newLayout = $client->duplicateLayout($layoutId, 'Event Copy - August');

// Delete layout (fails if has bookings)
$client->deleteLayout($layoutId);

// Force delete (use with caution!)
$client->deleteLayout($layoutId, true);`}
                      language="php"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="PHP: Configure Your Seat Fees" icon={Code}>
                    <CodeBlock 
                      code={`<?php
$client = new SeatPilotClient('YOUR_API_KEY');

// Get your current pricing configuration
$pricing = $client->getPricing();
echo "Your seat fee: £" . $pricing['data']['pricing']['seat_fee'] . "\\n";
echo "SeatPilot fee: £" . $pricing['data']['seatpilot_fee']['per_seat'] . "\\n";
echo "Your margin: £" . $pricing['data']['your_margin'] . "\\n";
echo "Plan: " . $pricing['data']['seatpilot_fee']['plan_name'] . "\\n";

// Update your pricing (charge organisers more to make margin)
$client->updatePricing([
    'seat_fee' => 0.80,  // You charge organisers £0.80/seat
    'currency' => 'GBP',
    'fee_name' => 'Seating Service Fee',
    'pass_to_organiser' => true,
    'pass_to_customer' => true  // Allow organisers to pass fee to customers
]);

// Calculate fees before processing booking
$calculation = $client->calculateFees(50);  // 50 seats
echo "For 50 seats:\\n";
echo "  Organiser pays you: £" . $calculation['data']['calculation']['total']['organiser_pays'] . "\\n";
echo "  You pay SeatPilot: £" . $calculation['data']['calculation']['total']['seatpilot_receives'] . "\\n";
echo "  Your profit: £" . $calculation['data']['calculation']['total']['your_profit'] . "\\n";

// Get available SeatPilot plans
$plans = $client->getPlans();
foreach ($plans['data']['plans'] as $plan) {
    echo $plan['name'] . ': ' . $plan['description'] . "\\n";
}`}
                      language="php"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="PHP: Embed Widget in Your Website" icon={Code}>
                    <CodeBlock 
                      code={`<?php
// In your event page controller
$layoutId = getLayoutIdForEvent($eventId);  // From your database

// Generate embed code for booking widget
$widgetHtml = '<iframe 
    src="${baseUrl}/widget/' . $layoutId . '" 
    width="100%" 
    height="600" 
    frameborder="0"
    allow="payment"
></iframe>';

// Or embed the layout builder for organisers
$builderHtml = '<iframe 
    src="${baseUrl}/embed/builder?api_key=' . $apiKey . '&organiser_id=' . $organiserId . '&theme=light" 
    width="100%" 
    height="700" 
    frameborder="0"
></iframe>';
?>

<!-- In your Blade/PHP template -->
<div class="seating-widget">
    <h2>Select Your Seats</h2>
    <?php echo $widgetHtml; ?>
</div>

<!-- Or for organiser dashboard -->
<div class="layout-builder">
    <h2>Design Your Seating Layout</h2>
    <?php echo $builderHtml; ?>
</div>`}
                      language="php"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="PHP: Complete Integration Example (Laravel)" icon={Code}>
                    <CodeBlock 
                      code={`<?php
// app/Services/SeatPilotService.php
namespace App\\Services;

class SeatPilotService {
    private $client;
    
    public function __construct() {
        $this->client = new \\SeatPilotClient(config('services.seatpilot.api_key'));
    }
    
    public function createEventLayout($event, $organiser) {
        return $this->client->createLayout([
            'name' => $event->name . ' - Seating',
            'venue_name' => $event->venue->name,
            'organiser_id' => 'ORG_' . $organiser->id,
            'organiser_name' => $organiser->name,
            'event_id' => 'EVT_' . $event->id,
            'event_name' => $event->name,
            'event_date' => $event->date->format('Y-m-d'),
            'event_time' => $event->time,
            'quick_create' => [
                'type' => 'theater',
                'rows' => $event->venue->default_rows,
                'seats_per_row' => $event->venue->default_seats_per_row
            ]
        ]);
    }
}

// app/Http/Controllers/EventController.php
public function createSeatingLayout(Event $event) {
    $seatpilot = new SeatPilotService();
    $result = $seatpilot->createEventLayout($event, auth()->user()->organiser);
    
    if ($result['status'] === 200) {
        $event->update([
            'seatpilot_layout_id' => $result['data']['layout_id'],
            'seatpilot_widget_url' => $result['data']['widget_url']
        ]);
        
        return redirect()->back()->with('success', 'Seating layout created!');
    }
    
    return redirect()->back()->with('error', 'Failed to create layout');
}

// In your Blade view
<div id="seating-selection">
    <iframe 
        src="{{ $event->seatpilot_widget_url }}" 
        width="100%" 
        height="600" 
        frameborder="0">
    </iframe>
</div>`}
                      language="php"
                    />
                  </ExpandableSection>
                </Card>
              </div>
            )}
            
            {/* Pricing Setup Section - Category-Based Ticket Pricing */}
            {activeSection === "pricing" && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Category-Based Ticket Pricing</h1>
                  <p className="text-gray-600">Set ticket prices per <strong>category</strong> (zone). Categories are automatically created from zones in the layout builder. No need to know individual seat references.</p>
                </div>
                
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>How it works:</strong><br/>
                    1. Categories = Zone names from the layout builder (VIP, Premium, Standard, etc.)<br/>
                    2. For bus layouts, all seats default to a single "General" category<br/>
                    3. For bus/simple layouts, you can optionally reassign categories per row via API<br/>
                    4. Set price per category &mdash; SeatPilot maps it to all seats automatically<br/>
                    5. Widget shows prices to customers. Calculate API returns total with fees.
                  </p>
                </div>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Step 1: Check Categories in Your Layout</h2>
                  <p className="text-gray-600 mb-4 text-sm">
                    Get all categories (zones) and their seat counts for a layout. This tells you what category names to use when setting prices.
                  </p>
                  <ExpandableSection title="GET /api/external/layouts/:layout_id/categories" icon={Code} defaultOpen>
                    <CodeBlock 
                      code={`curl -X GET "${baseUrl}/api/external/layouts/\${LAYOUT_ID}/categories" \\
  -H "X-API-Key: YOUR_API_KEY"

# Response (theater/concert layout with zones):
{
  "layout_id": "layout_abc123",
  "categories": [
    { "category": "VIP", "total_seats": 50, "available": 48, "booked": 2, "held": 0, "price": null },
    { "category": "Premium", "total_seats": 100, "available": 95, "booked": 5, "held": 0, "price": null },
    { "category": "Standard", "total_seats": 200, "available": 190, "booked": 8, "held": 2, "price": null }
  ],
  "currency": "GBP",
  "has_pricing": false
}

# Response (bus layout - single unified category):
{
  "layout_id": "layout_bus456",
  "categories": [
    { "category": "General", "total_seats": 48, "available": 48, "booked": 0, "held": 0, "price": null }
  ],
  "currency": "GBP",
  "has_pricing": false
}`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Step 1b: Reassign Categories (Optional)</h2>
                  <p className="text-gray-600 mb-4 text-sm">
                    For layouts without zones (buses, small venues), you can optionally reassign categories per row. Bus layouts default to a single "General" category so you typically just set one price.
                  </p>
                  <ExpandableSection title="PUT /api/external/layouts/:layout_id/categories" icon={Code}>
                    <CodeBlock 
                      code={`# Reassign categories by row (optional - only if you need different pricing zones)
curl -X PUT "${baseUrl}/api/external/layouts/\${LAYOUT_ID}/categories" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '[
    { "rows": ["1", "2"], "category": "Front" },
    { "rows": ["3", "4", "5", "6", "7", "8", "9", "10"], "category": "Standard" },
    { "rows": ["11", "12"], "category": "Back" }
  ]'`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Step 1c: Get Seat Details by Category</h2>
                  <p className="text-gray-600 mb-4 text-sm">
                    Fetch all seats with their IDs, references, rows, and status — grouped by category. Use this to create tickets in your system with specific seat assignments.
                  </p>
                  <ExpandableSection title="GET /api/external/layouts/:layout_id/seats" icon={Code} defaultOpen>
                    <CodeBlock 
                      code={`# Get all seats grouped by category
curl -X GET "${baseUrl}/api/external/layouts/\${LAYOUT_ID}/seats" \\
  -H "X-API-Key: YOUR_API_KEY"

# Filter by category
curl -X GET "${baseUrl}/api/external/layouts/\${LAYOUT_ID}/seats?category=VIP" \\
  -H "X-API-Key: YOUR_API_KEY"

# Filter by status
curl -X GET "${baseUrl}/api/external/layouts/\${LAYOUT_ID}/seats?status=available" \\
  -H "X-API-Key: YOUR_API_KEY"

# Response:
{
  "layout_id": "layout_abc123",
  "layout_name": "Main Hall",
  "total_seats": 200,
  "categories": [
    {
      "category": "VIP",
      "total": 50,
      "available": 45,
      "booked": 5,
      "held": 0,
      "price": 75.00,
      "currency": "GBP",
      "seats": [
        { "seat_id": "uuid-001", "seat_ref": "A1", "label": "A1", "row": 0, "side": "left", "status": "available" },
        { "seat_id": "uuid-002", "seat_ref": "A2", "label": "A2", "row": 0, "side": "left", "status": "booked" }
      ]
    },
    {
      "category": "Standard",
      "total": 150,
      "available": 140,
      "booked": 8,
      "held": 2,
      "price": 25.00,
      "currency": "GBP",
      "seats": [...]
    }
  ],
  "currency": "GBP",
  "has_pricing": true
}`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Step 1d: Assign Categories by Seat ID</h2>
                  <p className="text-gray-600 mb-4 text-sm">
                    Assign categories to individual seats by seat_id or seat_ref. Use this when you need fine-grained control over which seats belong to which ticket category.
                  </p>
                  <ExpandableSection title="PUT /api/external/layouts/:layout_id/seats/categories" icon={Code}>
                    <CodeBlock 
                      code={`curl -X PUT "${baseUrl}/api/external/layouts/\${LAYOUT_ID}/seats/categories" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '[
    { "seat_id": "uuid-001", "category": "VIP" },
    { "seat_ref": "A1", "category": "Premium" },
    { "seat_refs": ["B1", "B2", "B3"], "category": "Standard", "quantity": 3 }
  ]'

# Response:
{
  "success": true,
  "layout_id": "layout_abc123",
  "total_updated": 5,
  "assignments": [
    {
      "category": "VIP",
      "quantity": 1,
      "seats": [{ "seat_id": "uuid-001", "seat_ref": "A1", "label": "A1", "row": 0, "status": "available", "category": "VIP" }]
    },
    {
      "category": "Standard",
      "quantity": 3,
      "seats": [
        { "seat_id": "uuid-010", "seat_ref": "B1", "label": "B1", "row": 1, "status": "available", "category": "Standard" },
        { "seat_id": "uuid-011", "seat_ref": "B2", "label": "B2", "row": 1, "status": "available", "category": "Standard" },
        { "seat_id": "uuid-012", "seat_ref": "B3", "label": "B3", "row": 1, "status": "available", "category": "Standard" }
      ]
    }
  ]
}`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>


                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Step 1e: Rename or Delete a Category</h2>
                  <p className="text-gray-600 mb-4 text-sm">
                    Rename a category across every seat in a layout, or delete a category entirely (seats are reassigned to <code>General</code> by default). Pricing entries are migrated/removed automatically.
                  </p>
                  <ExpandableSection title="PATCH /api/external/layouts/:layout_id/categories/:category_name" icon={Code}>
                    <CodeBlock 
                      code={`# Rename "Standard" -> "Economy"
curl -X PATCH "${baseUrl}/api/external/layouts/\${LAYOUT_ID}/categories/Standard" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "new_name": "Economy" }'

# Response:
{
  "success": true,
  "old_name": "Standard",
  "new_name": "Economy",
  "seats_updated": 150,
  "message": "Renamed 'Standard' to 'Economy' across 150 seats"
}`}
                      language="bash"
                    />
                  </ExpandableSection>

                  <ExpandableSection title="DELETE /api/external/layouts/:layout_id/categories/:category_name" icon={Code}>
                    <CodeBlock 
                      code={`# Delete "VIP" — seats reassigned to General (default)
curl -X DELETE "${baseUrl}/api/external/layouts/\${LAYOUT_ID}/categories/VIP" \\
  -H "X-API-Key: YOUR_API_KEY"

# Delete "VIP" — reassign seats to a specific category instead
curl -X DELETE "${baseUrl}/api/external/layouts/\${LAYOUT_ID}/categories/VIP?reassign_to=Premium" \\
  -H "X-API-Key: YOUR_API_KEY"

# Response:
{
  "success": true,
  "deleted_category": "VIP",
  "reassigned_to": "Premium",
  "seats_reassigned": 50,
  "pricing_removed": true,
  "message": "Deleted 'VIP' — 50 seats moved to 'Premium'"
}`}
                      language="bash"
                    />
                  </ExpandableSection>

                  <ExpandableSection title="SDK: sp.renameCategory() / sp.deleteCategory()" icon={Code}>
                    <CodeBlock 
                      code={`// SDK equivalents (v2.1.0+)
const sp = SeatPilot.init({ apiKey: 'YOUR_KEY' });

// Rename
await sp.renameCategory(layoutId, 'Standard', 'Economy');

// Delete (seats default to "General")
await sp.deleteCategory(layoutId, 'VIP');

// Delete and move orphaned seats to a specific category
await sp.deleteCategory(layoutId, 'VIP', 'Premium');`}
                      language="javascript"
                    />
                  </ExpandableSection>
                </Card>


                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Step 1f: Full Seat Management (CRUD)</h2>
                  <p className="text-gray-600 mb-4 text-sm">
                    Complete control over individual seats. Add, update, delete, or bulk update seats via API or SDK.
                  </p>
                  <ExpandableSection title="POST — Add Seats" icon={Code}>
                    <CodeBlock 
                      code={`// API: Add new seats to a layout
curl -X POST "${baseUrl}/api/external/layouts/\${LAYOUT_ID}/seats" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '[
    { "seat_ref": "F1", "category": "VIP", "label": "Front Row 1", "row": 5 },
    { "seat_ref": "F2", "category": "VIP", "label": "Front Row 2", "row": 5 }
  ]'

// SDK:
await sp.addSeats('LAYOUT_ID', [
  { seat_ref: 'F1', category: 'VIP', label: 'Front Row 1', row: 5 },
  { seat_ref: 'F2', category: 'VIP', row: 5 }
]);`}
                      language="bash"
                    />
                  </ExpandableSection>
                  <ExpandableSection title="GET — Single Seat Details" icon={Code}>
                    <CodeBlock 
                      code={`// API: Get single seat (by seat_id or seat_ref)
curl -X GET "${baseUrl}/api/external/layouts/\${LAYOUT_ID}/seats/A1" \\
  -H "X-API-Key: YOUR_API_KEY"

// Response: { seat_id, seat_ref, label, category, row, side, status, price, currency }

// SDK:
const seat = await sp.getSeat('LAYOUT_ID', 'A1');`}
                      language="bash"
                    />
                  </ExpandableSection>
                  <ExpandableSection title="PUT — Update Single Seat" icon={Code}>
                    <CodeBlock 
                      code={`// API: Update category, label, or status
curl -X PUT "${baseUrl}/api/external/layouts/\${LAYOUT_ID}/seats/A1" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "category": "Premium", "label": "Aisle Seat 1" }'

// SDK:
await sp.updateSeat('LAYOUT_ID', 'A1', { category: 'Premium', label: 'Aisle Seat 1' });

// Disable a seat (won't show as available):
await sp.updateSeat('LAYOUT_ID', 'A1', { status: 'disabled' });`}
                      language="bash"
                    />
                  </ExpandableSection>
                  <ExpandableSection title="PATCH — Bulk Update Seats" icon={Code}>
                    <CodeBlock 
                      code={`// API: Update multiple seats at once
curl -X PATCH "${baseUrl}/api/external/layouts/\${LAYOUT_ID}/seats" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '[
    { "seat_ref": "A1", "status": "disabled" },
    { "seat_ref": "A2", "category": "VIP", "label": "VIP Aisle" },
    { "seat_ref": "B1", "category": "Premium" }
  ]'

// SDK:
await sp.updateSeats('LAYOUT_ID', [
  { seat_ref: 'A1', status: 'disabled' },
  { seat_ref: 'A2', category: 'VIP', label: 'VIP Aisle' },
  { seat_ref: 'B1', category: 'Premium' }
]);`}
                      language="bash"
                    />
                  </ExpandableSection>
                  <ExpandableSection title="DELETE — Remove Seat" icon={Code}>
                    <CodeBlock 
                      code={`// API: Delete a seat permanently
curl -X DELETE "${baseUrl}/api/external/layouts/\${LAYOUT_ID}/seats/A1" \\
  -H "X-API-Key: YOUR_API_KEY"

// SDK:
await sp.deleteSeat('LAYOUT_ID', 'A1');`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>


                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Step 2: Set Ticket Prices by Category</h2>
                  <p className="text-gray-600 mb-4 text-sm">
                    Pass prices using the category names from Step 1. All seats in each category get the price automatically.
                  </p>
                  <ExpandableSection title="PUT /api/external/events/:layout_id/pricing" icon={Code} defaultOpen>
                    <CodeBlock 
                      code={`# For a theater layout with multiple categories:
curl -X PUT "${baseUrl}/api/external/events/\${LAYOUT_ID}/pricing" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "pricing": [
      { "category": "VIP", "price": 75.00 },
      { "category": "Premium", "price": 45.00 },
      { "category": "Standard", "price": 25.00 }
    ],
    "currency": "GBP"
  }'

# For a bus layout (single category = one price for all seats):
curl -X PUT "${baseUrl}/api/external/events/\${LAYOUT_ID}/pricing" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "pricing": [
      { "category": "General", "price": 15.00 }
    ],
    "currency": "GBP"
  }'

# Response:
{
  "success": true,
  "layout_id": "layout_abc123",
  "categories_priced": 3,
  "message": "Pricing updated successfully"
}

# Optional: Override specific seats (e.g., front row premium)
{
  "pricing": [
    { "category": "VIP", "price": 75.00 },
    { "category": "Standard", "price": 25.00 }
  ],
  "currency": "GBP",
  "seat_overrides": [
    { "seat_ref": "VIP - A1", "price": 100.00 },
    { "seat_ref": "VIP - A2", "price": 100.00 }
  ]
}`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Step 2b: Configure Service Fees (Company-Wide)</h2>
                  <p className="text-gray-600 mb-4 text-sm">
                    Set your ticketing company's service fee. This applies to all layouts and is calculated as: <code className="bg-gray-100 px-1 rounded">(ticket_price x percent / 100) + fixed</code>
                  </p>
                  <ExpandableSection title="PUT /api/external/pricing" icon={Code} defaultOpen>
                    <CodeBlock 
                      code={`curl -X PUT "${baseUrl}/api/external/pricing" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "service_fee_percent": 5.0,
    "service_fee_fixed": 0.30
  }'

# Example: For a £25.00 ticket
# Service fee = (25.00 x 5% = 1.25) + 0.30 = £1.55
# Seat fee (SeatPilot) = £0.50
# Customer pays: £25.00 + £1.55 + £0.50 = £27.05`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Step 3: Calculate Total at Checkout</h2>
                  <p className="text-gray-600 mb-4 text-sm">
                    When a customer selects seats, calculate the total including ticket prices, service fees, and seat fees. Pass the selected seat references (shown in the widget).
                  </p>
                  <ExpandableSection title="POST /api/external/events/:layout_id/calculate" icon={Code} defaultOpen>
                    <CodeBlock 
                      code={`curl -X POST "${baseUrl}/api/external/events/\${LAYOUT_ID}/calculate" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '["VIP - A1", "VIP - A2", "Standard - C5"]'

# Response:
{
  "layout_id": "layout_abc123",
  "currency": "GBP",
  "items": [
    { "seat_ref": "VIP - A1", "category": "VIP", "ticket_price": 75.00, "service_fee": 4.05, "seat_fee": 0.50, "total": 79.55 },
    { "seat_ref": "VIP - A2", "category": "VIP", "ticket_price": 75.00, "service_fee": 4.05, "seat_fee": 0.50, "total": 79.55 },
    { "seat_ref": "Standard - C5", "category": "Standard", "ticket_price": 25.00, "service_fee": 1.55, "seat_fee": 0.50, "total": 27.05 }
  ],
  "summary": {
    "seats_selected": 3,
    "ticket_subtotal": 175.00,
    "total_service_fees": 9.65,
    "seat_fee_per_seat": 0.50,
    "total_seat_fees": 1.50,
    "total_fees": 11.15,
    "grand_total": 186.15
  },
  "fee_config": {
    "service_fee_percent": 5.0,
    "service_fee_fixed": 0.30,
    "seat_fee_per_seat": 0.50
  },
  "category_breakdown": [
    { "category": "VIP", "count": 2, "subtotal": 150.00 },
    { "category": "Standard", "count": 1, "subtotal": 25.00 }
  ]
}`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Get Current Pricing</h2>
                  <p className="text-gray-600 mb-4 text-sm">
                    Retrieve the current pricing configuration for a layout.
                  </p>
                  <ExpandableSection title="GET /api/external/events/:layout_id/pricing" icon={Code}>
                    <CodeBlock 
                      code={`curl -X GET "${baseUrl}/api/external/events/\${LAYOUT_ID}/pricing" \\
  -H "X-API-Key: YOUR_API_KEY"

# Response:
{
  "layout_id": "layout_abc123",
  "pricing": [
    { "category": "VIP", "price": 75.00, "label": null },
    { "category": "Standard", "price": 25.00, "label": null }
  ],
  "currency": "GBP",
  "seat_overrides": [],
  "has_pricing": true
}`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>

                <Card className="p-6 bg-amber-50 border-amber-200">
                  <h3 className="font-semibold text-gray-900 mb-3">Bus Layout Pricing - Quick Summary</h3>
                  <p className="text-sm text-gray-700 mb-3">
                    Bus layouts are the simplest to price. All seats default to a single "General" category, so you only need to set one price:
                  </p>
                  <ol className="text-sm text-gray-700 space-y-1 list-decimal ml-4">
                    <li>Create bus layout via API (all seats auto-assigned to "General")</li>
                    <li>Set one price: <code className="bg-amber-100 px-1 rounded">{"{"}"pricing": [{"{"}"category": "General", "price": 15.00{"}"}], "currency": "GBP"{"}"}</code></li>
                    <li>Done! All 48 seats are now priced at 15.00</li>
                  </ol>
                  <p className="text-sm text-gray-600 mt-3">
                    Want different zones on a bus? Use Step 1b to reassign rows to categories before setting prices.
                  </p>
                </Card>
              </div>
            )}
            
            {/* Ticket Types Section */}
            {activeSection === "ticket-types" && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Ticket Types API</h1>
                  <p className="text-gray-600">Assign your platform's ticket types (VIP, Standard, Early Bird, etc.) to seats, rows, or zones. Each ticket type carries its own pricing, sales window, and visibility.</p>
                </div>
                
                <Card className="p-6 bg-blue-50 border-blue-200">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-blue-900">How It Works</h3>
                      <ol className="text-sm text-blue-800 mt-2 space-y-1 list-decimal ml-4">
                        <li>Add your ticket types to a layout (name, price, sales window)</li>
                        <li>Assign ticket types to zones, rows, or individual seats</li>
                        <li>Query availability — each seat returns its applicable ticket types with pricing</li>
                        <li>Seat-level assignments override row-level, which override zone-level</li>
                      </ol>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Add Ticket Types</h2>
                  <p className="text-sm text-gray-600 mb-3">Add one or more ticket types to a layout. If a ticket_type_id already exists, it will be updated.</p>
                  <CodeBlock title="POST /api/external/layouts/:layout_id/ticket-types" code={`curl -X POST ${baseUrl}/api/external/layouts/LAYOUT_ID/ticket-types \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "ticket_types": [
      {
        "ticket_type_id": "tt_vip_001",
        "type": "paid",
        "name": "VIP",
        "price": 75.00,
        "currency": "GBP",
        "available_quantity": 50,
        "sales_start": "2025-03-01T00:00:00Z",
        "sales_end": "2025-06-01T23:59:59Z",
        "description": "Premium VIP experience",
        "visibility": "public",
        "show_remaining": true,
        "is_addon": false,
        "main_ticket_required": false
      },
      {
        "ticket_type_id": "tt_std_001",
        "type": "paid",
        "name": "Standard",
        "price": 25.00,
        "currency": "GBP",
        "available_quantity": 200,
        "sales_start": "2025-03-01T00:00:00Z",
        "sales_end": "2025-06-01T23:59:59Z",
        "visibility": "public"
      },
      {
        "ticket_type_id": "tt_earlybird",
        "type": "paid",
        "name": "Early Bird",
        "price": 15.00,
        "currency": "GBP",
        "available_quantity": 100,
        "sales_start": "2025-01-01T00:00:00Z",
        "sales_end": "2025-02-28T23:59:59Z",
        "visibility": "public"
      }
    ]
  }'`} />
                  
                  <div className="mt-4 bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Ticket Type Fields</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-3 text-gray-600">Field</th>
                            <th className="text-left py-2 px-3 text-gray-600">Required</th>
                            <th className="text-left py-2 px-3 text-gray-600">Description</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-700">
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">ticket_type_id</td><td className="py-2 px-3">Yes</td><td className="py-2 px-3">Your platform's ticket type ID</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">type</td><td className="py-2 px-3">Yes</td><td className="py-2 px-3">Any classification (e.g., paid, free, donation, or custom)</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">name</td><td className="py-2 px-3">Yes</td><td className="py-2 px-3">Any name you want (e.g., "VIP", "Early Bird", "Group Pass")</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">price</td><td className="py-2 px-3">Yes</td><td className="py-2 px-3">Price (0 for free tickets)</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">available_quantity</td><td className="py-2 px-3">Yes</td><td className="py-2 px-3">Total available for this type</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">sales_start</td><td className="py-2 px-3">Yes</td><td className="py-2 px-3">ISO 8601 sales window start</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">sales_end</td><td className="py-2 px-3">Yes</td><td className="py-2 px-3">ISO 8601 sales window end</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">visibility</td><td className="py-2 px-3">No</td><td className="py-2 px-3">public, private, or hidden (default: public)</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">description</td><td className="py-2 px-3">No</td><td className="py-2 px-3">Ticket description</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">age_group</td><td className="py-2 px-3">No</td><td className="py-2 px-3">Age group restriction</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">show_remaining</td><td className="py-2 px-3">No</td><td className="py-2 px-3">Show remaining count (default: true)</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">is_addon</td><td className="py-2 px-3">No</td><td className="py-2 px-3">Sell as add-on (default: false)</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">main_ticket_required</td><td className="py-2 px-3">No</td><td className="py-2 px-3">Requires main ticket (default: false)</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">service_fee_percent</td><td className="py-2 px-3">No</td><td className="py-2 px-3">Your platform's service fee % (e.g., 2.0)</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">service_fee_fixed</td><td className="py-2 px-3">No</td><td className="py-2 px-3">Fixed service fee amount (e.g., 0.40)</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">processing_fee_percent</td><td className="py-2 px-3">No</td><td className="py-2 px-3">Processing fee % (e.g., 2.10)</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">processing_fee_fixed</td><td className="py-2 px-3">No</td><td className="py-2 px-3">Fixed processing fee (e.g., 0.30)</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">absorb_fee</td><td className="py-2 px-3">No</td><td className="py-2 px-3">Organiser absorbs fees (default: false)</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">ticket_per_order_min</td><td className="py-2 px-3">No</td><td className="py-2 px-3">Min tickets per order (default: 1)</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">ticket_per_order_max</td><td className="py-2 px-3">No</td><td className="py-2 px-3">Max tickets per order (default: 10)</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">donation_name</td><td className="py-2 px-3">No</td><td className="py-2 px-3">Donation name (for donation tickets)</td></tr>
                          <tr className="border-b"><td className="py-2 px-3 font-mono text-xs">chart_category_key</td><td className="py-2 px-3">No</td><td className="py-2 px-3">Key to link ticket to a seating zone/category</td></tr>
                          <tr><td className="py-2 px-3 font-mono text-xs">sale_start_type</td><td className="py-2 px-3">No</td><td className="py-2 px-3">scheduled or manual (default: scheduled)</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Assign Ticket Types to Seats</h2>
                  <p className="text-sm text-gray-600 mb-3">Assign ticket types at zone, row, or individual seat level. Seat-level overrides row-level, which overrides zone-level.</p>
                  <CodeBlock title="POST /api/external/layouts/:layout_id/assign-ticket-types" code={`curl -X POST ${baseUrl}/api/external/layouts/LAYOUT_ID/assign-ticket-types \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "assignments": [
      {
        "ticket_type_id": "tt_vip_001",
        "assignment_type": "zone",
        "target": "VIP Zone",
        "priority": 1
      },
      {
        "ticket_type_id": "tt_earlybird",
        "assignment_type": "zone",
        "target": "VIP Zone",
        "priority": 0
      },
      {
        "ticket_type_id": "tt_std_001",
        "assignment_type": "row",
        "target": "C",
        "priority": 1
      },
      {
        "ticket_type_id": "tt_std_001",
        "assignment_type": "row",
        "target": "D",
        "priority": 1
      }
    ],
    "replace_existing": false
  }'`} />
                  
                  <Card className="mt-4 p-4 bg-amber-50 border-amber-200">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-amber-900">Early Bird + Standard Pattern</h4>
                        <p className="text-sm text-amber-800 mt-1">
                          Assign both an Early Bird and Standard ticket type to the same zone/row with different priorities.
                          Set the Early Bird's <code className="bg-amber-100 px-1 rounded">sales_end</code> earlier than Standard's <code className="bg-amber-100 px-1 rounded">sales_start</code>.
                          When querying availability with <code className="bg-amber-100 px-1 rounded">active_only=true</code>, 
                          only the ticket type currently within its sales window will appear.
                        </p>
                      </div>
                    </div>
                  </Card>
                  
                  <div className="mt-4 bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Assignment Types</h4>
                    <div className="space-y-2 text-sm text-gray-700">
                      <div className="flex gap-2"><code className="bg-gray-200 px-2 py-0.5 rounded font-mono text-xs">zone</code><span>All seats in the zone get this ticket type. Target = zone label (e.g., "VIP Zone")</span></div>
                      <div className="flex gap-2"><code className="bg-gray-200 px-2 py-0.5 rounded font-mono text-xs">row</code><span>All seats in the row get this ticket type. Target = row letter (e.g., "A", "B")</span></div>
                      <div className="flex gap-2"><code className="bg-gray-200 px-2 py-0.5 rounded font-mono text-xs">seat</code><span>Specific seat. Target = seat_ref (e.g., "VIP Zone - A1")</span></div>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Get Availability with Ticket Types</h2>
                  <p className="text-sm text-gray-600 mb-3">Query seat availability with resolved ticket types. Each seat includes its applicable ticket types based on assignments.</p>
                  <CodeBlock title="GET /api/external/layouts/:layout_id/availability" code={`curl ${baseUrl}/api/external/layouts/LAYOUT_ID/availability?active_only=true \\
  -H "X-API-Key: YOUR_API_KEY"`} />
                  
                  <div className="mt-4">
                    <h4 className="font-medium text-gray-900 mb-2">Response Example</h4>
                    <CodeBlock language="json" code={`{
  "layout_id": "abc-123",
  "total_seats": 100,
  "status_breakdown": {"available": 85, "held": 5, "booked": 10},
  "seatpilot_fee": {
    "per_seat_fee": 0.50,
    "currency": "GBP",
    "plan_name": "Pay As You Go",
    "has_allowance": false,
    "monthly_allowance": 0,
    "overage_fee": 0.50,
    "note": "Add per_seat_fee to your platform fees for final cost_to_buyer"
  },
  "seats": [
    {
      "seat_ref": "VIP Zone - A1",
      "status": "available",
      "ticket_types": [
        {
          "ticket_type_id": "tt_earlybird",
          "name": "Early Bird",
          "price": 15.00,
          "currency": "GBP",
          "service_fee_percent": 2.0,
          "service_fee_fixed": 0.40,
          "processing_fee_percent": 2.10,
          "processing_fee_fixed": 0.30,
          "matched_by": "zone",
          "priority": 0
        }
      ]
    }
  ],
  "ticket_types": [ ... ],
  "timestamp": "2025-03-15T10:30:00Z"
}

// Fee calculation example:
// given_cost (ticket price):     £15.00
// + your service_fee:            £0.70  (2% of 15 + £0.40)
// + your processing_fee:         £0.62  (2.10% of 15 + £0.30)
// + seatpilot per_seat_fee:      £0.50
// = cost_to_buyer:               £16.82`} />
                  </div>
                </Card>
                
                <Card className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Other Endpoints</h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-medium text-gray-900">Get Ticket Types</h3>
                      <CodeBlock code={`GET /api/external/layouts/:layout_id/ticket-types?active_only=false`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Update a Ticket Type</h3>
                      <CodeBlock code={`PUT /api/external/layouts/:layout_id/ticket-types/:ticket_type_id
Body: { "price": 80.00, "name": "VIP Premium" }`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Delete a Ticket Type</h3>
                      <CodeBlock code={`DELETE /api/external/layouts/:layout_id/ticket-types/:ticket_type_id`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Get Assignments</h3>
                      <CodeBlock code={`GET /api/external/layouts/:layout_id/assign-ticket-types`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Remove Assignments</h3>
                      <CodeBlock code={`DELETE /api/external/layouts/:layout_id/assign-ticket-types?ticket_type_id=tt_vip_001`} />
                      <p className="text-xs text-gray-500 mt-1">Filter by ticket_type_id, assignment_type, or target. Omit all to clear everything.</p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">JavaScript SDK</h2>
                  <p className="text-sm text-gray-600 mb-3">If using the builder.js SDK, ticket type methods are available on the builder instance:</p>
                  <CodeBlock language="javascript" code={`const builder = SeatPilot.builder({
  apiKey: 'YOUR_API_KEY',
  container: '#seatpilot-builder',
  userId: 'ORG_001',
  layoutId: 'LAYOUT_ID',    // Pass to edit existing layout
  onReady: () => console.log('Builder ready'),
  onSave: (layout) => console.log('Saved:', layout.layout_id, layout.seat_count)
});

// Add ticket types
await builder.addTicketTypes('LAYOUT_ID', [
  {
    ticket_type_id: 'tt_vip',
    type: 'paid',
    name: 'VIP',
    price: 75,
    currency: 'GBP',
    available_quantity: 50,
    sales_start: '2025-03-01T00:00:00Z',
    sales_end: '2025-06-01T23:59:59Z'
  }
]);

// Assign to zones/rows/seats
await builder.assignTicketTypes('LAYOUT_ID', [
  { ticket_type_id: 'tt_vip', assignment_type: 'zone', target: 'VIP Zone', priority: 1 },
  { ticket_type_id: 'tt_std', assignment_type: 'row', target: 'C', priority: 1 }
]);

// Get availability with ticket types resolved per seat
const availability = await builder.getAvailabilityWithTicketTypes('LAYOUT_ID');
console.log(availability.seats[0].ticket_types);`} />
                </Card>
              </div>
            )}
            
            {/* Email Settings Section */}
            {activeSection === "emails" && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">📧 Email Settings API</h1>
                  <p className="text-gray-600">Configure company-branded emails so your customers receive notifications from your company, not SeatPilot.</p>
                </div>
                
                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Why Configure Email Settings?</h2>
                  
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-4">
                    <p className="text-violet-800 font-semibold flex items-center gap-2">
                      <span className="text-xl">🏷️</span> White-Label Exclusive Feature
                    </p>
                    <p className="text-violet-600 text-sm mt-1">
                      Custom email branding requires an active White-Label subscription (£20/month).
                    </p>
                  </div>
                  
                  <p className="text-gray-600 mb-4">
                    When you integrate SeatPilot, your event organizers (customers) receive email notifications when:
                  </p>
                  <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                    <li><strong>Layout Created</strong> - A new seating layout is created</li>
                    <li><strong>Layout Published</strong> - A layout goes live and is ready for bookings</li>
                    <li><strong>Layout Deleted</strong> - A layout is removed from the system</li>
                  </ul>
                  <p className="text-gray-600">
                    <strong>Without White-Label:</strong> Emails come from SeatPilot<br/>
                    <strong>With White-Label:</strong> Emails come from <strong>your company</strong>, providing a seamless branded experience.
                  </p>
                </Card>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Get Current Email Settings</h2>
                  <CodeBlock 
                    title="GET /api/subscriptions/external/email-settings"
                    language="bash"
                    code={`curl -X GET "${getBaseUrl()}/api/subscriptions/external/email-settings" \\
  -H "X-API-Key: YOUR_API_KEY"`}
                  />
                  <p className="text-gray-600 mt-4 mb-2"><strong>Response:</strong></p>
                  <CodeBlock 
                    language="json"
                    code={`{
  "use_company_branding": false,
  "sender_email": null,
  "sender_name": "Your Company Name",
  "reply_to_email": null,
  "is_configured": false
}`}
                  />
                </Card>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Update Email Settings</h2>
                  <CodeBlock 
                    title="PUT /api/subscriptions/external/email-settings"
                    language="bash"
                    code={`curl -X PUT "${getBaseUrl()}/api/subscriptions/external/email-settings" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "use_company_branding": true,
    "sender_email": "notifications@yourcompany.com",
    "sender_name": "YourCompany Tickets",
    "reply_to_email": "support@yourcompany.com"
  }'`}
                  />
                  <p className="text-gray-600 mt-4 mb-2"><strong>Request Body:</strong></p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3">Field</th>
                          <th className="text-left py-2 px-3">Type</th>
                          <th className="text-left py-2 px-3">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b">
                          <td className="py-2 px-3 font-mono text-violet-600">use_company_branding</td>
                          <td className="py-2 px-3">boolean</td>
                          <td className="py-2 px-3">Enable/disable company-branded emails</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 px-3 font-mono text-violet-600">sender_email</td>
                          <td className="py-2 px-3">string</td>
                          <td className="py-2 px-3">Email address to send from (e.g., notifications@yourcompany.com)</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 px-3 font-mono text-violet-600">sender_name</td>
                          <td className="py-2 px-3">string</td>
                          <td className="py-2 px-3">Display name for the sender (e.g., "YourCompany Tickets")</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 px-3 font-mono text-violet-600">reply_to_email</td>
                          <td className="py-2 px-3">string</td>
                          <td className="py-2 px-3">Email address for replies (optional)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">🐘 PHP Example</h2>
                  <CodeBlock 
                    language="php"
                    code={`<?php
class SeatPilotEmailSettings {
    private $apiKey;
    private $baseUrl = '${getBaseUrl()}';
    
    public function __construct($apiKey) {
        $this->apiKey = $apiKey;
    }
    
    /**
     * Configure company-branded emails
     */
    public function configureEmails($senderEmail, $senderName, $replyToEmail = null) {
        $data = [
            'use_company_branding' => true,
            'sender_email' => $senderEmail,
            'sender_name' => $senderName,
            'reply_to_email' => $replyToEmail
        ];
        
        $ch = curl_init($this->baseUrl . '/api/subscriptions/external/email-settings');
        curl_setopt($ch, CURLOPT_CUSTOMREQUEST, 'PUT');
        curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
        curl_setopt($ch, CURLOPT_HTTPHEADER, [
            'X-API-Key: ' . $this->apiKey,
            'Content-Type: application/json'
        ]);
        curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
        
        $response = curl_exec($ch);
        curl_close($ch);
        
        return json_decode($response, true);
    }
    
    /**
     * Disable company-branded emails (revert to SeatPilot)
     */
    public function disableBranding() {
        $data = ['use_company_branding' => false];
        
        $ch = curl_init($this->baseUrl . '/api/subscriptions/external/email-settings');
        curl_setopt($ch, CURLOPT_CUSTOMREQUEST, 'PUT');
        curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
        curl_setopt($ch, CURLOPT_HTTPHEADER, [
            'X-API-Key: ' . $this->apiKey,
            'Content-Type: application/json'
        ]);
        curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
        
        $response = curl_exec($ch);
        curl_close($ch);
        
        return json_decode($response, true);
    }
}

// Usage Example
$emailSettings = new SeatPilotEmailSettings('your_api_key_here');

// Enable company-branded emails
$result = $emailSettings->configureEmails(
    'tickets@myticketcompany.com',
    'MyTicketCompany Notifications',
    'support@myticketcompany.com'
);

// Now all layout emails to your customers will come from your company!
?>`}
                  />
                </Card>

                <Card className="p-6 bg-blue-50 border-blue-200">
                  <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Info className="w-5 h-5 text-blue-600" />
                    Important Notes
                  </h2>
                  <ul className="list-disc list-inside text-gray-700 space-y-2">
                    <li><strong>Email Deliverability:</strong> For best results, use a verified domain email address</li>
                    <li><strong>SPF/DKIM:</strong> To avoid spam filters, consider setting up SPF and DKIM records for your domain</li>
                    <li><strong>Testing:</strong> After configuration, create a test layout to verify emails are sent from your company</li>
                    <li><strong>Fallback:</strong> If your email settings are invalid, emails will fall back to SeatPilot sender</li>
                  </ul>
                </Card>
              </div>
            )}
            
            {/* Widget Embed Section */}
            {activeSection === "widget" && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Widget Embedding</h1>
                  <p className="text-gray-600">Embed the interactive seat selection widget in your checkout page.</p>
                </div>
                
                <Card className="p-6">
                  <ExpandableSection title="Basic Embed" icon={Code} defaultOpen>
                    <CodeBlock 
                      code={`<iframe 
  id="seatpilot-widget"
  src="${baseUrl}/widget/${sampleLayoutId}"
  width="100%" 
  height="600"
  frameborder="0"
  allow="payment"
></iframe>`}
                      language="html"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Widget URL Parameters" icon={Info}>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-3 font-medium">Parameter</th>
                            <th className="text-left py-2 px-3 font-medium">Type</th>
                            <th className="text-left py-2 px-3 font-medium">Description</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          <tr><td className="py-2 px-3 font-mono text-violet-600">showPrices</td><td className="py-2 px-3">boolean</td><td className="py-2 px-3">Show zone prices on map</td></tr>
                          <tr><td className="py-2 px-3 font-mono text-violet-600">currency</td><td className="py-2 px-3">string</td><td className="py-2 px-3">GBP, USD, EUR, INR</td></tr>
                          <tr><td className="py-2 px-3 font-mono text-violet-600">maxSeats</td><td className="py-2 px-3">number</td><td className="py-2 px-3">Max seats per selection</td></tr>
                          <tr><td className="py-2 px-3 font-mono text-violet-600">hideBranding</td><td className="py-2 px-3">boolean</td><td className="py-2 px-3">Hide SeatPilot branding (Pro)</td></tr>
                          <tr><td className="py-2 px-3 font-mono text-violet-600">primaryColor</td><td className="py-2 px-3">hex</td><td className="py-2 px-3">Primary accent color</td></tr>
                          <tr><td className="py-2 px-3 font-mono text-violet-600">event_id</td><td className="py-2 px-3">string</td><td className="py-2 px-3">YOUR event ID for tracking</td></tr>
                          <tr><td className="py-2 px-3 font-mono text-violet-600">lang</td><td className="py-2 px-3">string</td><td className="py-2 px-3">Language code (en, es, fr)</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </ExpandableSection>
                  
                  <ExpandableSection title="Listening for Widget Events" icon={Code}>
                    <CodeBlock 
                      code={`window.addEventListener('message', function(event) {
  // Security: verify origin
  if (event.origin !== '${baseUrl}') return;
  
  const { type, ...data } = event.data;
  
  switch(type) {
    case 'widget_ready':
      console.log('Widget loaded, can send configuration');
      break;
      
    case 'seat_selected':
      console.log('Seat selected:', data.seat);
      break;
      
    case 'seat_deselected':
      console.log('Seat deselected:', data.seat);
      break;
      
    case 'seats_selected':
      // All currently selected seats
      console.log('Selected seats:', data.seats);
      break;
      
    case 'checkout_requested':
      // User clicked checkout - proceed to payment
      console.log('Checkout with seats:', data.seats);
      startCheckoutFlow(data.seats);
      break;
      
    case 'error':
      console.error('Widget error:', data.message);
      break;
  }
});`}
                      language="javascript"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Sending Commands to Widget" icon={Code}>
                    <CodeBlock 
                      code={`const widget = document.getElementById('seatpilot-widget');

// Pre-select specific seats
widget.contentWindow.postMessage({
  type: 'select_seats',
  seats: ['A-1', 'A-2', 'A-3']
}, '${baseUrl}');

// Clear all selections
widget.contentWindow.postMessage({
  type: 'clear_selection'
}, '${baseUrl}');

// Lock specific seats (already booked)
widget.contentWindow.postMessage({
  type: 'lock_seats',
  seats: ['B-5', 'B-6']
}, '${baseUrl}');

// Update availability in real-time
widget.contentWindow.postMessage({
  type: 'update_availability',
  available: ['A-1', 'A-2'],
  unavailable: ['A-3', 'A-4']
}, '${baseUrl}');`}
                      language="javascript"
                    />
                  </ExpandableSection>
                </Card>
              </div>
            )}
            
            {/* API Reference Section */}
            {activeSection === "api" && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">API Reference</h1>
                  <p className="text-gray-600">Complete REST API documentation for SeatPilot integration.</p>
                </div>
                
                <Card className="p-6">
                  <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg mb-6">
                    <p className="text-sm text-violet-800">
                      <strong>Base URL:</strong> <code className="bg-white px-2 py-0.5 rounded">{baseUrl}/api</code>
                    </p>
                  </div>
                  
                  <ExpandableSection title="Reserve Seats (Hold)" icon={Terminal} defaultOpen>
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-bold rounded">POST</span>
                        <code className="text-sm">/api/public/reserve</code>
                      </div>
                      <p className="text-sm text-gray-600">
                        Hold seats temporarily before payment. Reserved for the specified duration.
                      </p>
                      <CodeBlock 
                        title="Request"
                        code={`curl -X POST "${baseUrl}/api/public/reserve" \\
  -H "X-API-Key: ${sampleApiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "layout_id": "${sampleLayoutId}",
    "event_id": "YOUR_EVENT_ID",
    "seats": [
      {"seat_ref": "A-1"},
      {"seat_ref": "A-2"}
    ],
    "hold_duration_minutes": 15,
    "metadata": {
      "customer_session": "session_abc"
    }
  }'`}
                        language="bash"
                      />
                      <CodeBlock 
                        title="Response"
                        code={`{
  "success": true,
  "data": {
    "hold_token": "hold_xyz789abc",
    "seats_held": 2,
    "expires_at": "2025-12-20T10:45:00Z"
  }
}`}
                        language="json"
                      />
                    </div>
                  </ExpandableSection>
                  
                  <ExpandableSection title="Confirm Booking" icon={Terminal}>
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-bold rounded">POST</span>
                        <code className="text-sm">/api/public/confirm</code>
                      </div>
                      <p className="text-sm text-gray-600">
                        Confirm booking after successful payment.
                      </p>
                      <CodeBlock 
                        title="Request"
                        code={`curl -X POST "${baseUrl}/api/public/confirm" \\
  -H "X-API-Key: ${sampleApiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "hold_token": "hold_xyz789abc",
    "booking_id": "YOUR_BOOKING_ID",
    "event_id": "YOUR_EVENT_ID",
    "customer": {
      "email": "customer@example.com",
      "name": "John Doe"
    },
    "payment": {
      "method": "stripe",
      "transaction_id": "pi_abc123",
      "amount": 300.00,
      "currency": "GBP"
    },
    "metadata": {
      "promo_code": "SUMMER20"
    }
  }'`}
                        language="bash"
                      />
                    </div>
                  </ExpandableSection>
                  
                  <ExpandableSection title="Release Hold" icon={Terminal}>
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-bold rounded">DELETE</span>
                        <code className="text-sm">/api/public/reserve/{"hold_token"}</code>
                      </div>
                      <CodeBlock 
                        code={`curl -X DELETE "${baseUrl}/api/public/reserve/hold_xyz789abc" \\
  -H "X-API-Key: ${sampleApiKey}"`}
                        language="bash"
                      />
                    </div>
                  </ExpandableSection>
                  
                  <ExpandableSection title="Get Availability" icon={Terminal}>
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded">GET</span>
                        <code className="text-sm">/api/public/availability/{"{layout_id}"}</code>
                      </div>
                      <CodeBlock 
                        code={`curl -X GET "${baseUrl}/api/public/availability/${sampleLayoutId}?event_id=YOUR_EVENT_ID" \\
  -H "X-API-Key: ${sampleApiKey}"`}
                        language="bash"
                      />
                      <CodeBlock 
                        title="Response"
                        code={`{
  "success": true,
  "layout_id": "${sampleLayoutId}",
  "event_id": "YOUR_EVENT_ID",
  "summary": {
    "total": 500,
    "available": 450,
    "held": 30,
    "booked": 20
  },
  "seats": [
    { "seat_ref": "A-1", "status": "available", "zone": "VIP" },
    { "seat_ref": "A-2", "status": "held", "zone": "VIP" },
    { "seat_ref": "A-3", "status": "booked", "zone": "VIP" }
  ]
}`}
                        language="json"
                      />
                    </div>
                  </ExpandableSection>
                  
                  <ExpandableSection title="Cancel Booking" icon={Terminal}>
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-bold rounded">DELETE</span>
                        <code className="text-sm">/api/public/bookings/{"{booking_ref}"}</code>
                      </div>
                      <CodeBlock 
                        code={`curl -X DELETE "${baseUrl}/api/public/bookings/bk_abc123" \\
  -H "X-API-Key: ${sampleApiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"reason": "Customer requested cancellation"}'`}
                        language="bash"
                      />
                    </div>
                  </ExpandableSection>
                </Card>
              </div>
            )}
            
            {/* Events & IDs Section */}
            {activeSection === "events" && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Events & Reference IDs</h1>
                  <p className="text-gray-600">Understanding how to link layouts to your events and track bookings.</p>
                </div>
                
                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Reference ID Types</h2>
                  <div className="overflow-x-auto mb-6">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left py-3 px-4 font-medium">ID Type</th>
                          <th className="text-left py-3 px-4 font-medium">Format</th>
                          <th className="text-left py-3 px-4 font-medium">Created By</th>
                          <th className="text-left py-3 px-4 font-medium">Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        <tr><td className="py-3 px-4 font-mono text-violet-600">layout_id</td><td className="py-3 px-4"><code>layout_xxxxx</code></td><td className="py-3 px-4">SeatPilot</td><td className="py-3 px-4">Your seating layout identifier</td></tr>
                        <tr className="bg-green-50"><td className="py-3 px-4 font-mono text-green-600 font-bold">event_id</td><td className="py-3 px-4">Your format</td><td className="py-3 px-4 font-bold text-green-700">YOU</td><td className="py-3 px-4">Your event reference</td></tr>
                        <tr className="bg-green-50"><td className="py-3 px-4 font-mono text-green-600 font-bold">booking_id</td><td className="py-3 px-4">Your format</td><td className="py-3 px-4 font-bold text-green-700">YOU</td><td className="py-3 px-4">Your booking/order reference</td></tr>
                        <tr><td className="py-3 px-4 font-mono text-violet-600">booking_ref</td><td className="py-3 px-4"><code>bk_xxxxx</code></td><td className="py-3 px-4">SeatPilot</td><td className="py-3 px-4">SeatPilot booking reference</td></tr>
                        <tr><td className="py-3 px-4 font-mono text-violet-600">transaction_id</td><td className="py-3 px-4"><code>txn_xxxxx</code></td><td className="py-3 px-4">SeatPilot</td><td className="py-3 px-4">Audit trail ID</td></tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
                    <p className="text-sm text-amber-800">
                      <strong>💡 Important:</strong> Always include YOUR <code>event_id</code> and <code>booking_id</code> in API calls. This allows you to track everything in your system and makes debugging much easier.
                    </p>
                  </div>
                  
                  <ExpandableSection title="Link Layout to Your Event" icon={Code} defaultOpen>
                    <p className="text-sm text-gray-600 mb-3">When creating an event in your platform, link it to a SeatPilot layout:</p>
                    <CodeBlock 
                      code={`curl -X POST "${baseUrl}/api/public/events/link" \\
  -H "X-API-Key: ${sampleApiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "layout_id": "${sampleLayoutId}",
    "event_id": "EVT-2025-001",
    "event_name": "Summer Concert 2025",
    "event_date": "2025-07-15T19:00:00Z",
    "metadata": {
      "venue": "City Stadium",
      "artist": "Famous Band",
      "category": "music"
    }
  }'`}
                      language="bash"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Create Booking with Your IDs" icon={Code}>
                    <CodeBlock 
                      code={`curl -X POST "${baseUrl}/api/public/bookings" \\
  -H "X-API-Key: ${sampleApiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "layout_id": "${sampleLayoutId}",
    "event_id": "EVT-2025-001",
    "booking_id": "BK-123456789",
    "customer_email": "customer@example.com",
    "seats": ["A-1", "A-2", "A-3"],
    "metadata": {
      "customer_name": "John Doe",
      "phone": "+44 7700 900000",
      "order_ref": "ORD-999",
      "promo_code": "SUMMER20",
      "purchase_channel": "web",
      "ticket_name": "standard"
    }
  }'`}
                      language="bash"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Find Booking by Your Reference" icon={Code}>
                    <CodeBlock 
                      code={`# Find by YOUR booking ID
curl -X GET "${baseUrl}/api/public/bookings/by-reference/BK-123456789" \\
  -H "X-API-Key: ${sampleApiKey}"

# Find by SeatPilot booking ref
curl -X GET "${baseUrl}/api/public/bookings/bk_xyz789" \\
  -H "X-API-Key: ${sampleApiKey}"`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>
                
                {/* Metadata Section */}
                <Card className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Custom Metadata</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Send any custom data using the <code className="bg-gray-100 px-1 rounded">metadata</code> field. This data is stored with the booking and returned in webhooks/logs.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">Common Metadata Fields</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• <code>customer_name</code> - Customer name</li>
                        <li>• <code>order_ref</code> - Your order ID</li>
                        <li>• <code>promo_code</code> - Discount code applied</li>
                        <li>• <code>purchase_channel</code> - web/mobile/kiosk</li>
                        <li>• <code>ticket_name</code> - Ticket name (VIP/Standard/etc)</li>
                        <li>• <code>phone</code> - Customer phone</li>
                      </ul>
                    </div>
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">Best Practices</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>✅ Always include your IDs for tracking</li>
                        <li>✅ Store SeatPilot refs in your database</li>
                        <li>✅ Use metadata for debugging info</li>
                        <li>✅ Keep metadata JSON-serializable</li>
                        <li>✅ Log request_id for support tickets</li>
                      </ul>
                    </div>
                  </div>
                </Card>
              </div>
            )}
            
            {/* Debugging Section */}
            {activeSection === "debugging" && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Debug Mode & Logging</h1>
                  <p className="text-gray-600">Comprehensive debugging tools for development and troubleshooting.</p>
                </div>
                
                <Card className="p-6">
                  <ExpandableSection title="Enable Debug Mode" icon={Terminal} defaultOpen>
                    <p className="text-sm text-gray-600 mb-3">Add <code className="bg-gray-100 px-1 rounded">X-Debug: true</code> header to get detailed response info:</p>
                    <CodeBlock 
                      code={`curl -X POST "${baseUrl}/api/public/bookings" \\
  -H "X-API-Key: ${sampleApiKey}" \\
  -H "X-Debug: true" \\
  -H "Content-Type: application/json" \\
  -d '{
    "layout_id": "${sampleLayoutId}",
    "event_id": "YOUR_EVENT_ID",
    "booking_id": "YOUR_BOOKING_ID",
    "seats": ["A-1", "A-2"]
  }'`}
                      language="bash"
                    />
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800">
                        Debug responses include: request_id, processing time, validation checks, and execution trace.
                      </p>
                    </div>
                  </ExpandableSection>
                  
                  <ExpandableSection title="Debug Response Example" icon={Code}>
                    <CodeBlock 
                      code={`{
  "success": true,
  "data": { "booking_ref": "bk_xyz789" },
  "debug": {
    "request_id": "req_abc123def456",
    "timestamp": "2025-12-20T10:35:00Z",
    "processing_time_ms": 45,
    "your_references": {
      "event_id": "YOUR_EVENT_ID",
      "booking_id": "YOUR_BOOKING_ID"
    },
    "validation": {
      "passed": true,
      "checks": [
        "✓ API key valid",
        "✓ Layout exists and is published",
        "✓ All seats available",
        "✓ Event linked to layout"
      ]
    },
    "trace": [
      "[0ms] Request received",
      "[5ms] API key validated",
      "[12ms] Layout fetched",
      "[20ms] Seats availability checked",
      "[35ms] Booking created",
      "[45ms] Response sent"
    ]
  }
}`}
                      language="json"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="View Request Logs" icon={Terminal}>
                    <CodeBlock 
                      code={`# Get all logs from last 7 days
curl -X GET "${baseUrl}/api/public/logs?from=2025-12-13" \\
  -H "X-API-Key: ${sampleApiKey}"

# Filter by your event ID
curl -X GET "${baseUrl}/api/public/logs?event_id=YOUR_EVENT_ID" \\
  -H "X-API-Key: ${sampleApiKey}"

# Filter by your booking ID  
curl -X GET "${baseUrl}/api/public/logs?booking_id=YOUR_BOOKING_ID" \\
  -H "X-API-Key: ${sampleApiKey}"

# Get only errors
curl -X GET "${baseUrl}/api/public/logs?type=error" \\
  -H "X-API-Key: ${sampleApiKey}"`}
                      language="bash"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Transaction Audit Trail" icon={Terminal}>
                    <CodeBlock 
                      code={`# Get full transaction history
curl -X GET "${baseUrl}/api/public/transactions?event_id=YOUR_EVENT_ID" \\
  -H "X-API-Key: ${sampleApiKey}"

# Response includes transaction types:
# - booking_created
# - seat_held  
# - seat_released
# - booking_cancelled
# - pricing_updated
# - event_linked`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>
                
                {/* Best Practices */}
                <Card className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Debug Best Practices</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-violet-50 rounded-lg">
                      <h4 className="font-medium text-violet-900 mb-2">✓ Always Include</h4>
                      <ul className="text-sm text-violet-800 space-y-1">
                        <li>• Your event_id in all requests</li>
                        <li>• Your booking_id for tracking</li>
                        <li>• Metadata for debugging info</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <h4 className="font-medium text-amber-900 mb-2">⚠ Store These</h4>
                      <ul className="text-sm text-amber-800 space-y-1">
                        <li>• request_id from every response</li>
                        <li>• transaction_id for audit trail</li>
                        <li>• booking_ref from SeatPilot</li>
                      </ul>
                    </div>
                  </div>
                </Card>
              </div>
            )}
            
            {/* Webhooks Section */}
            {activeSection === "webhooks" && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Webhooks</h1>
                  <p className="text-gray-600">Receive real-time notifications when events occur.</p>
                </div>
                
                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Available Events</h2>
                  <div className="overflow-x-auto mb-6">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 font-medium">Event</th>
                          <th className="text-left py-2 px-3 font-medium">Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        <tr><td className="py-2 px-3 font-mono text-violet-600">booking.created</td><td className="py-2 px-3">New booking confirmed</td></tr>
                        <tr><td className="py-2 px-3 font-mono text-violet-600">booking.cancelled</td><td className="py-2 px-3">Booking was cancelled</td></tr>
                        <tr><td className="py-2 px-3 font-mono text-violet-600">seats.held</td><td className="py-2 px-3">Seats temporarily reserved</td></tr>
                        <tr><td className="py-2 px-3 font-mono text-violet-600">seats.released</td><td className="py-2 px-3">Hold expired, seats released</td></tr>
                        <tr><td className="py-2 px-3 font-mono text-violet-600">seats.availability_low</td><td className="py-2 px-3">Less than 10% seats remaining</td></tr>
                        <tr><td className="py-2 px-3 font-mono text-violet-600">seats.sold_out</td><td className="py-2 px-3">All seats booked</td></tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <ExpandableSection title="Webhook Payload" icon={Code} defaultOpen>
                    <CodeBlock 
                      code={`{
  "webhook_id": "whk_abc123xyz",
  "event_type": "booking.created",
  "event_id": "evt_def456",
  "timestamp": "2025-12-20T10:35:00Z",
  "data": {
    "booking_ref": "bk_xyz789",
    "your_event_id": "YOUR_EVENT_ID",
    "your_booking_id": "YOUR_BOOKING_ID",
    "layout_id": "${sampleLayoutId}",
    "seats": ["A-1", "A-2"],
    "customer_email": "customer@example.com",
    "status": "confirmed",
    "metadata": {
      "customer_name": "John Doe",
      "order_ref": "ORD-12345"
    }
  }
}`}
                      language="json"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Verify Webhook Signature" icon={Code}>
                    <CodeBlock 
                      code={`// Node.js example
const crypto = require('crypto');

function verifyWebhook(payload, signature, timestamp, secret) {
  const signedPayload = \`\${timestamp}.\${JSON.stringify(payload)}\`;
  const expectedSignature = crypto
    .createHmac('sha256', secret)
    .update(signedPayload)
    .digest('hex');
  
  return \`sha256=\${expectedSignature}\` === signature;
}

// Express.js
app.post('/webhooks/seatpilot', (req, res) => {
  const signature = req.headers['x-seatpilot-signature'];
  const timestamp = req.headers['x-seatpilot-timestamp'];
  
  if (!verifyWebhook(req.body, signature, timestamp, WEBHOOK_SECRET)) {
    return res.status(401).send('Invalid signature');
  }
  
  // Process webhook...
  res.status(200).send('OK');
});`}
                      language="javascript"
                    />
                  </ExpandableSection>
                </Card>
              </div>
            )}
            

            {/* Analytics API Section */}
            {activeSection === "analytics" && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Analytics API</h1>
                  <p className="text-gray-600">Pull real-time analytics into your own dashboard. All endpoints require <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">X-API-Key</code> header.</p>
                </div>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Combined Dashboard (Recommended)</h2>
                  <p className="text-gray-600 mb-4 text-sm">
                    Get everything in a single API call — overview stats, top layouts with seat counts, and organiser breakdown.
                  </p>
                  <ExpandableSection title="GET /api/external/analytics/dashboard" icon={Code} defaultOpen>
                    <CodeBlock 
                      code={`curl -X GET "${baseUrl}/api/external/analytics/dashboard" \\
  -H "X-API-Key: YOUR_API_KEY"

# Response:
{
  "overview": {
    "total_layouts": 27,
    "published_layouts": 25,
    "total_seats": 1250,
    "booked_seats": 340,
    "available_seats": 890,
    "held_seats": 20,
    "occupancy_rate": 27.2,
    "total_organisers": 5,
    "total_potential_revenue": 25000.00,
    "total_earned_revenue": 6800.00
  },
  "top_layouts": [
    {
      "layout_id": "abc123",
      "name": "Main Stage",
      "organiser_name": "Festival Corp",
      "seats": { "total": 500, "available": 350, "booked": 140, "held": 10 },
      "occupancy_percent": 28.0,
      "potential_revenue": 12500.00,
      "earned_revenue": 3500.00,
      "has_pricing": true
    }
  ],
  "organisers": [
    {
      "organiser_id": "org_123",
      "organiser_name": "Festival Corp",
      "layout_count": 5,
      "total_seats": 800,
      "booked_seats": 200
    }
  ]
}`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Overview Stats</h2>
                  <ExpandableSection title="GET /api/external/analytics/overview" icon={Code}>
                    <CodeBlock 
                      code={`curl -X GET "${baseUrl}/api/external/analytics/overview" \\
  -H "X-API-Key: YOUR_API_KEY"

# Returns: total_layouts, total_seats, booked_seats, occupancy_rate, etc.`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Per-Layout Analytics</h2>
                  <p className="text-gray-600 mb-4 text-sm">Filterable by organiser and publish status.</p>
                  <ExpandableSection title="GET /api/external/analytics/layouts" icon={Code}>
                    <CodeBlock 
                      code={`# All layouts
curl -X GET "${baseUrl}/api/external/analytics/layouts" \\
  -H "X-API-Key: YOUR_API_KEY"

# Filter by organiser
curl -X GET "${baseUrl}/api/external/analytics/layouts?organiser_id=ORG_123" \\
  -H "X-API-Key: YOUR_API_KEY"

# Only published layouts
curl -X GET "${baseUrl}/api/external/analytics/layouts?is_published=true" \\
  -H "X-API-Key: YOUR_API_KEY"`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Booking Reports</h2>
                  <p className="text-gray-600 mb-4 text-sm">Booking history with category breakdown. Supports pagination.</p>
                  <ExpandableSection title="GET /api/external/analytics/bookings" icon={Code}>
                    <CodeBlock 
                      code={`curl -X GET "${baseUrl}/api/external/analytics/bookings?limit=20&offset=0" \\
  -H "X-API-Key: YOUR_API_KEY"

# Filter by layout
curl -X GET "${baseUrl}/api/external/analytics/bookings?layout_id=LAYOUT_ID" \\
  -H "X-API-Key: YOUR_API_KEY"`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Revenue Breakdown</h2>
                  <p className="text-gray-600 mb-4 text-sm">Potential vs earned revenue, service fees, by layout and category.</p>
                  <ExpandableSection title="GET /api/external/analytics/revenue" icon={Code}>
                    <CodeBlock 
                      code={`curl -X GET "${baseUrl}/api/external/analytics/revenue" \\
  -H "X-API-Key: YOUR_API_KEY"

# Filter by layout
curl -X GET "${baseUrl}/api/external/analytics/revenue?layout_id=LAYOUT_ID" \\
  -H "X-API-Key: YOUR_API_KEY"`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Usage Trends</h2>
                  <p className="text-gray-600 mb-4 text-sm">API usage and booking trends over time.</p>
                  <ExpandableSection title="GET /api/external/analytics/trends" icon={Code}>
                    <CodeBlock 
                      code={`# Daily trends (last 30 days)
curl -X GET "${baseUrl}/api/external/analytics/trends?period=daily&days=30" \\
  -H "X-API-Key: YOUR_API_KEY"

# Weekly trends (last 90 days)
curl -X GET "${baseUrl}/api/external/analytics/trends?period=weekly&days=90" \\
  -H "X-API-Key: YOUR_API_KEY"

# Monthly trends (last year)
curl -X GET "${baseUrl}/api/external/analytics/trends?period=monthly&days=365" \\
  -H "X-API-Key: YOUR_API_KEY"`}
                      language="bash"
                    />
                  </ExpandableSection>
                </Card>
              </div>
            )}
            

            {/* Code Examples Section */}
            {activeSection === "examples" && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Code Examples</h1>
                  <p className="text-gray-600">Complete integration examples for common scenarios.</p>
                </div>
                
                <Card className="p-6">
                  <ExpandableSection title="Complete Booking Flow (Node.js)" icon={Code} defaultOpen>
                    <CodeBlock 
                      code={`const axios = require('axios');

const SEATPILOT_API = '${baseUrl}/api';
const API_KEY = process.env.SEATPILOT_API_KEY;

async function bookSeats(eventId, seats, customer) {
  const bookingId = \`BK-\${Date.now()}\`;
  
  try {
    // 1. Reserve seats (hold for 15 mins)
    const holdRes = await axios.post(\`\${SEATPILOT_API}/public/reserve\`, {
      layout_id: 'YOUR_LAYOUT_ID',
      event_id: eventId,
      seats: seats.map(s => ({ seat_ref: s })),
      hold_duration_minutes: 15
    }, {
      headers: { 'X-API-Key': API_KEY }
    });
    
    const { hold_token } = holdRes.data.data;
    
    // 2. Process payment (your payment gateway)
    const paymentResult = await processPayment(customer, seats);
    
    // 3. Confirm booking
    const confirmRes = await axios.post(\`\${SEATPILOT_API}/public/confirm\`, {
      hold_token,
      booking_id: bookingId,
      event_id: eventId,
      customer: {
        email: customer.email,
        name: customer.name
      },
      payment: {
        method: 'stripe',
        transaction_id: paymentResult.id,
        amount: paymentResult.amount,
        currency: 'GBP'
      },
      metadata: {
        order_ref: bookingId,
        purchase_channel: 'web'
      }
    }, {
      headers: { 'X-API-Key': API_KEY }
    });
    
    return confirmRes.data;
    
  } catch (error) {
    if (error.response?.data?.error?.code === 'SEATS_UNAVAILABLE') {
      throw new Error('Some seats are no longer available');
    }
    throw error;
  }
}`}
                      language="javascript"
                    />
                  </ExpandableSection>
                  
                  <ExpandableSection title="Python Integration" icon={Code}>
                    <CodeBlock 
                      code={`import requests
import os
from datetime import datetime

class SeatPilotClient:
    def __init__(self):
        self.api_key = os.environ['SEATPILOT_API_KEY']
        self.base_url = '${baseUrl}/api'
    
    def _request(self, method, endpoint, data=None, debug=False):
        headers = {
            'X-API-Key': self.api_key,
            'Content-Type': 'application/json'
        }
        if debug:
            headers['X-Debug'] = 'true'
        
        response = requests.request(
            method, f"{self.base_url}{endpoint}",
            json=data, headers=headers
        )
        return response.json()
    
    def reserve_seats(self, layout_id, event_id, seats, minutes=15):
        return self._request('POST', '/public/reserve', {
            'layout_id': layout_id,
            'event_id': event_id,
            'seats': [{'seat_ref': s} for s in seats],
            'hold_duration_minutes': minutes
        })
    
    def confirm_booking(self, hold_token, booking_id, event_id, customer, payment):
        return self._request('POST', '/public/confirm', {
            'hold_token': hold_token,
            'booking_id': booking_id,
            'event_id': event_id,
            'customer': customer,
            'payment': payment
        })
    
    def get_availability(self, layout_id, event_id=None):
        endpoint = f'/public/availability/{layout_id}'
        if event_id:
            endpoint += f'?event_id={event_id}'
        return self._request('GET', endpoint)

# Usage
client = SeatPilotClient()

# Check availability
availability = client.get_availability('layout_abc123', 'EVT-2025-001')
print(f"Available seats: {availability['data']['summary']['available']}")

# Reserve seats
hold = client.reserve_seats('layout_abc123', 'EVT-2025-001', ['A-1', 'A-2'])
print(f"Hold token: {hold['data']['hold_token']}")`}
                      language="python"
                    />
                  </ExpandableSection>
                </Card>
              </div>
            )}
            
            {/* FAQs Section */}
            {activeSection === "faq" && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Frequently Asked Questions</h1>
                  <p className="text-gray-600">Common questions about integrating SeatPilot.</p>
                </div>
                
                <Card className="p-6">
                  <ExpandableSection title="How do I get my layout_id?" icon={HelpCircle} defaultOpen>
                    <p className="text-sm text-gray-600">
                      Create a layout in your SeatPilot dashboard, then find the layout_id in the layout settings or by calling the layouts API endpoint.
                    </p>
                  </ExpandableSection>
                  
                  <ExpandableSection title="How long are seats held during checkout?" icon={HelpCircle}>
                    <p className="text-sm text-gray-600">
                      You control the hold duration (default: 15 minutes). Set <code>hold_duration_minutes</code> in the reserve request. Unreserved holds automatically release.
                    </p>
                  </ExpandableSection>
                  
                  <ExpandableSection title="Can I use the same layout for multiple events?" icon={HelpCircle}>
                    <p className="text-sm text-gray-600">
                      Yes! Use the <code>event_id</code> parameter to track availability separately for each event using the same layout.
                    </p>
                  </ExpandableSection>
                  
                  <ExpandableSection title="How do I handle race conditions?" icon={HelpCircle}>
                    <p className="text-sm text-gray-600">
                      SeatPilot uses optimistic locking. If two users try to book the same seat simultaneously, one will receive a <code>SEATS_UNAVAILABLE</code> error. Always handle this gracefully in your UI.
                    </p>
                  </ExpandableSection>
                  
                  <ExpandableSection title="What happens if a hold expires?" icon={HelpCircle}>
                    <p className="text-sm text-gray-600">
                      Seats are automatically released back to availability. If you try to confirm an expired hold, you'll get a <code>HOLD_EXPIRED</code> error.
                    </p>
                  </ExpandableSection>
                  
                  <ExpandableSection title="How do I test in sandbox mode?" icon={HelpCircle}>
                    <p className="text-sm text-gray-600">
                      Use your test API key (starts with <code>sk_test_</code>) for sandbox mode. All sandbox bookings are isolated and can be reset.
                    </p>
                  </ExpandableSection>
                </Card>
              </div>
            )}
          </main>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-gray-500">
          <p>© {new Date().getFullYear()} {BRAND_NAME}. All rights reserved.</p>
          <p className="mt-2">
            Need help? Contact <a href={`mailto:${supportEmail}`} className="text-violet-600 hover:underline">{supportEmail}</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
